import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Map, { Source, Layer, Popup, Marker, ScaleControl, MapRef } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { toPoint as mgrsToPoint } from 'mgrs';
import { PathfinderDataPoint, PathfinderRoute, CoTravelerResult, CoTravelerEncounter } from '../../services/pathfinderApi';
import { OverwatchArticle } from '../../services/overwatchApi';
import { Box, IconButton, Tooltip, Popover, Paper, Typography, Grid, Card, Divider, TextField, Button, Tabs, Tab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Explore as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  Satellite as SatelliteIcon,
  VisibilityOff as HideTimelineIcon,
  Visibility as ShowTimelineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  GpsFixed as GoToCoordIcon,
  SatelliteAlt as SatelliteAltIcon,
} from '@mui/icons-material';
import { DrawCircle } from './AreaSearch';
import { MapStyleType } from '../cop/MapStyleControl';
import { getStatusColors } from '../../theme';
import { useSettings } from '../../contexts/SettingsContext';

// Geographic bounds type for map viewport filtering
export interface MapBounds {
  north: number; // Max latitude
  south: number; // Min latitude
  east: number;  // Max longitude
  west: number;  // Min longitude
}

interface PathfinderMapGLProps {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  studentColors: Record<string, string>;
  mapSettings: {
    center: [number, number];
    zoom: number;
    style: string;
  };
  showDataPoints: boolean;
  showRouteLines: boolean;
  showDirectionArrows: boolean;
  showHeatmap: boolean;
  enableClustering: boolean;
  coTravelerResults?: CoTravelerResult[];
  showCoTravelerPoints?: boolean;
  showCoTravelerLines?: boolean;
  showCoTravelerHeatmap?: boolean;
  timeRange?: { start: Date; end: Date } | null;
  onMapChange?: (center: [number, number], zoom: number, bounds?: MapBounds) => void;
  followMode?: boolean; // If true, allow automatic camera updates
  extentUpdateTrigger?: number; // Force viewport update when this changes
  // Dwell locations
  dwellLocations?: any[];
  showDwells?: boolean;
  // Digital PoL
  digitalPoLData?: any[];
  showDigitalPoL?: boolean;
  // Area search / circle drawing props
  isDrawingMode?: boolean;
  drawingShapeType?: 'circle' | 'polygon';
  drawCircles?: DrawCircle[];
  defaultRadius?: number;
  measurementSystem?: 'metric' | 'imperial';
  onCircleDrawn?: (center: [number, number], radius: number, coordinates?: number[][][]) => void;
  onCircleUpdate?: (circleId: string, updates: Partial<DrawCircle>) => void; // For editing circles
  editingCircleId?: string | null; // ID of circle currently being edited
  // Highlight location (for zoom-to feature)
  highlightLocation?: { lat: number; lng: number; } | null;
  // Timeline slider visibility toggle
  timelineSliderHidden?: boolean;
  onToggleTimelineSlider?: () => void;
  // Callback when user searches for a location (address search or coordinates)
  onLocationSearch?: (location: { lat: number; lng: number; name: string; source: 'address' | 'coordinates' | 'mgrs' }) => void;
  // Overwatch article markers
  overwatchArticles?: OverwatchArticle[];
  showOverwatchArticles?: boolean;
  onArticleClick?: (article: OverwatchArticle) => void;
  exploreDeviceDetails?: Record<string, any>; // Additional device details from Explore API to pass to popups
  // Live satellite positions (TLE API via backend proxy)
  showSatelliteLayer?: boolean;
  onToggleSatelliteLayer?: () => void;
  satelliteGeoJson?: GeoJSON.FeatureCollection;
  satelliteTracksGeoJson?: GeoJSON.FeatureCollection;
  satelliteLayerLoading?: boolean;
  onSatelliteTrackSelect?: (norad: number | null) => void;
  selectedSatelliteNorad?: number | null;
}

const PathfinderMapGL: React.FC<PathfinderMapGLProps> = ({
  dataPoints,
  routes,
  studentColors,
  mapSettings,
  showDataPoints,
  showRouteLines,
  showDirectionArrows,
  showHeatmap,
  enableClustering,
  coTravelerResults = [],
  showCoTravelerPoints = false,
  showCoTravelerLines = false,
  showCoTravelerHeatmap = false,
  timeRange,
  onMapChange,
  followMode = false,
  extentUpdateTrigger = 0, // Default to 0
  // Dwell locations
  dwellLocations = [],
  showDwells = true,
  // Digital PoL
  digitalPoLData = [],
  showDigitalPoL = false,
  // Area search / circle drawing props
  isDrawingMode = false,
  drawingShapeType = 'circle',
  drawCircles = [],
  defaultRadius = 100,
  measurementSystem = 'metric',
  onCircleDrawn,
  onCircleUpdate,
  editingCircleId = null,
  // Highlight location
  highlightLocation = null,
  // Timeline slider visibility
  timelineSliderHidden = false,
  onToggleTimelineSlider,
  // Location search callback
  onLocationSearch,
  // Overwatch articles
  overwatchArticles = [],
  showOverwatchArticles = false,
  onArticleClick,
  exploreDeviceDetails = {},
  showSatelliteLayer = false,
  onToggleSatelliteLayer,
  satelliteGeoJson = { type: 'FeatureCollection', features: [] },
  satelliteTracksGeoJson = { type: 'FeatureCollection', features: [] },
  satelliteLayerLoading = false,
  onSatelliteTrackSelect,
  selectedSatelliteNorad = null,
}) => {
  // Theme hook for consistent colors
  const theme = useTheme();
  const statusColors = getStatusColors(theme);
  
  // Settings hook for user preferences
  const { settings } = useSettings();
  const trackPointSize = settings.visualization.trackPointSize ?? 4;

  const [viewport, setViewport] = useState({
    longitude: mapSettings.center[1] || 0, // Default to global view (prime meridian)
    latitude: mapSettings.center[0] || 0, // Default to global view (equator)
    zoom: mapSettings.zoom || 2 // Default to global zoom level
  });

  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [popupTab, setPopupTab] = useState(0);
  // Cluster popup state for carousel navigation
  const [clusterPoints, setClusterPoints] = useState<any[]>([]);
  const [clusterPointIndex, setClusterPointIndex] = useState(0);
  const mapRef = React.useRef<MapRef | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const geocoderRef = React.useRef<MapboxGeocoder | null>(null);
  const drawCreateHandlerRef = useRef<((e: any) => void) | null>(null);
  const drawUpdateHandlerRef = useRef<((e: any) => void) | null>(null);
  const styleLoadHandlerRef = useRef<(() => void) | null>(null);
  const styleLoadAttachedRef = useRef(false);
  
  // Highlight pulse state
  const [pulseLocation, setPulseLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pulseOpacity, setPulseOpacity] = useState(0.6);
  const [pulseRadius, setPulseRadius] = useState(50);
  
  // Circle editing state
  const [draggingCircleId, setDraggingCircleId] = useState<string | null>(null);
  const [dragType, setDragType] = useState<'center' | 'radius' | null>(null);
  const editingCircleIdRef = useRef<string | null>(null);
  const onCircleUpdateRef = useRef<typeof onCircleUpdate>(onCircleUpdate);
  
  // Keep ref in sync with prop
  useEffect(() => {
    editingCircleIdRef.current = editingCircleId || null;
  }, [editingCircleId]);

  // Keep latest callback without forcing downstream effects to re-run
  useEffect(() => {
    onCircleUpdateRef.current = onCircleUpdate;
  }, [onCircleUpdate]);
  
  // 3D mode state
  const [is3DMode, setIs3DMode] = useState(false);
  const is3DModeRef = useRef(is3DMode);
  const apply3DToCurrentStyleRef = useRef<() => void>(() => {});
  
  // Go to coordinates dialog state
  const [coordDialogAnchor, setCoordDialogAnchor] = useState<HTMLElement | null>(null);
  const [coordLatInput, setCoordLatInput] = useState('');
  const [coordLonInput, setCoordLonInput] = useState('');
  const [coordMgrsInput, setCoordMgrsInput] = useState('');
  
  // Fullscreen and measurement state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Debug: Log when showDataPoints changes
  useEffect(() => {
  }, [showDataPoints]);

  useEffect(() => {
  if (popupInfo) setPopupTab(0);
}, [popupInfo]);
  
  // Handle resize events to resize the map properly
  // This fixes the white space issue when panels are collapsed/expanded
  useEffect(() => {
    const handleResize = () => {
      // Give the CSS transition time to complete, then resize the map
      setTimeout(() => {
        const map = mapRef.current?.getMap();
        if (map) {
          map.resize();
        }
      }, 50); // Small delay to ensure container has resized
    };

    window.addEventListener('resize', handleResize);
    
    // Use ResizeObserver to detect container size changes (e.g., when bottom panel collapses)
    const container = mapContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        // Debounce the resize call
        handleResize();
      });
      resizeObserver.observe(container);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Sync viewport with mapSettings when they change from parent
  useEffect(() => {
    const newLat = mapSettings.center[0];
    const newLng = mapSettings.center[1];
    const newZoom = mapSettings.zoom;
    
    // Only update if values are meaningfully different to avoid loops
    if (
      Math.abs(viewport.latitude - newLat) > 0.0001 ||
      Math.abs(viewport.longitude - newLng) > 0.0001 ||
      Math.abs(viewport.zoom - newZoom) > 0.1
    ) {
      setViewport({
        latitude: newLat,
        longitude: newLng,
        zoom: newZoom
      });
      
      // Also fly to the location for smooth animation
      const map = mapRef.current?.getMap();
      if (map) {
        map.flyTo({
          center: [newLng, newLat],
          zoom: newZoom,
          duration: 1000
        });
      }
    }
  }, [mapSettings.center[0], mapSettings.center[1], mapSettings.zoom]);

  // Handle highlight location with pulsing animation
  useEffect(() => {
    if (highlightLocation) {
      setPulseLocation(highlightLocation);
      setPulseOpacity(0.7);
      setPulseRadius(30);
      
      // Animate the pulse - expand and fade out
      let frame = 0;
      const maxFrames = 30;
      const animate = () => {
        frame++;
        const progress = frame / maxFrames;
        
        // Expand radius from 30 to 150
        setPulseRadius(30 + (120 * progress));
        // Fade opacity from 0.7 to 0
        setPulseOpacity(0.7 * (1 - progress));
        
        if (frame < maxFrames) {
          requestAnimationFrame(animate);
        } else {
          // Reset after animation completes
          setTimeout(() => {
            setPulseLocation(null);
          }, 100);
        }
      };
      
      // Start animation after a brief delay for the fly-to to begin
      setTimeout(() => {
        requestAnimationFrame(animate);
      }, 500);
    }
  }, [highlightLocation]);
  
  // Cleanup geocoder on unmount
  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap();
      if (map && geocoderRef.current) {
        try {
          map.removeControl(geocoderRef.current);
        } catch (e) {
          // Control may already be removed
        }
        geocoderRef.current = null;
      }
      // Also clean up any stale DOM elements
      const existingGeocoder = document.querySelector('.mapboxgl-ctrl-geocoder');
      if (existingGeocoder) {
        existingGeocoder.remove();
      }
    };
  }, []);
  
  // Map style state (converted from old string to MapStyleType)
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => {
    const style = mapSettings.style.toLowerCase();
    if (style.includes('satellite-streets')) return 'satellite-streets';
    if (style.includes('satellite')) return 'satellite';
    if (style.includes('dark')) return 'dark';
    if (style.includes('light')) return 'light';
    if (style.includes('outdoors')) return 'outdoors';
    return 'streets';
  });
  
  // Map style popover state
  const [mapStyleAnchor, setMapStyleAnchor] = useState<HTMLButtonElement | null>(null);

  // Generate GeoJSON for drawn circles
  const circlesGeoJSON = useMemo(() => {
    if (drawCircles.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = drawCircles
      .filter(circle => {
        // Keep all visible circles/polygons visible
        // MapboxDraw will render its editable version on top for polygons being edited
        // But filter out result circles (Common Devices) - they shouldn't render as map circles
        return circle.visible && !circle.isResultCircle;
      })
      .map(circle => {
        let coordinates;
        
        if (circle.shapeType === 'polygon' && circle.coordinates) {
          // Use existing polygon coordinates (already in GeoJSON format)
          coordinates = circle.coordinates;
        } else {
          // Create a circle polygon approximation
          const center = circle.center; // [lat, lon]
          const radius = circle.radius;
          const points = 64; // Number of points to approximate the circle
          
          const circleCoords = [];
          for (let i = 0; i <= points; i++) {
            const angle = (i * 360) / points;
            const radians = (angle * Math.PI) / 180;
            
            // Simple approximation - for precise geodesic circles, a library like turf.js would be better
            const deltaLat = (radius / 111320) * Math.cos(radians); // ~111320 meters per degree latitude
            const deltaLon = (radius / (111320 * Math.cos((center[0] * Math.PI) / 180))) * Math.sin(radians);
            
            const lat = center[0] + deltaLat;
            const lon = center[1] + deltaLon;
            
            circleCoords.push([lon, lat]); // GeoJSON uses [lon, lat]
          }
          coordinates = [circleCoords];
        }
        
        // Calculate area label
        const areaSqM = Math.PI * circle.radius * circle.radius;
        let areaLabel = '';
        if (measurementSystem === 'imperial') {
           const sqMiles = areaSqM * 3.861e-7;
           areaLabel = sqMiles >= 0.1 ? `${sqMiles.toFixed(2)} mi²` : `${(areaSqM * 0.000247105).toFixed(2)} acres`;
        } else {
           areaLabel = areaSqM >= 1000000 ? `${(areaSqM / 1000000).toFixed(2)} km²` : `${Math.round(areaSqM).toLocaleString()} m²`;
        }
        
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Polygon' as const,
            coordinates: coordinates
          },
          properties: {
            id: circle.id,
            name: circle.name,
            areaLabel,
            shapeType: circle.shapeType,
            radius: circle.radius,
            centerLat: circle.center[0],
            centerLon: circle.center[1],
            isSaved: !!circle.geofenceId,
            hasResults: (circle.results && circle.results.length > 0),
            resultCount: circle.results ? circle.results.length : 0
          }
        };
      });

    return { type: 'FeatureCollection' as const, features };
  }, [drawCircles, measurementSystem]);

  // Generate GeoJSON for circle centers (for labels and interaction)
  const circleCentersGeoJSON = useMemo(() => {
    if (drawCircles.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = drawCircles
      .filter(circle => {
        // Keep all visible circles/polygons visible
        // MapboxDraw will render its editable version on top for polygons being edited
        // But filter out result circles (Common Devices) - they shouldn't render as map circles
        return circle.visible && !circle.isResultCircle;
      })
      .map(circle => {
        // Calculate area label
        const areaSqM = Math.PI * circle.radius * circle.radius;
        let areaLabel = '';
        if (measurementSystem === 'imperial') {
           const sqMiles = areaSqM * 3.861e-7;
           areaLabel = sqMiles >= 0.1 ? `${sqMiles.toFixed(2)} mi²` : `${(areaSqM * 0.000247105).toFixed(2)} acres`;
        } else {
           areaLabel = areaSqM >= 1000000 ? `${(areaSqM / 1000000).toFixed(2)} km²` : `${Math.round(areaSqM).toLocaleString()} m²`;
        }

        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [circle.center[1], circle.center[0]] // [lon, lat]
          },
          properties: {
            id: circle.id,
            name: circle.name,
            radius: circle.radius,
            areaLabel,
            hasResults: (circle.results && circle.results.length > 0),
            resultCount: circle.results ? circle.results.length : 0
          }
        };
      });

    return { type: 'FeatureCollection' as const, features };
  }, [drawCircles, measurementSystem]);

  // Convert data points to GeoJSON
  const pointsGeoJSON = useMemo(() => {
    // Logging disabled to prevent console spam
    
    if (dataPoints.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = dataPoints.map(point => {
      const studentId = point.studentId || point.deviceId;
      const deviceType = (point.deviceType || 'phone').toLowerCase();
      
      // Determine if this is a co-traveler track point
      const isCoTravelerTrack = (point as any).isCoTraveler === true;
      
      // Use different colors for co-traveler tracks
      let color = studentColors[point.deviceId] || '#FF6B6B';
      let opacity = 0.8;
      
      if (isCoTravelerTrack) {
        // Co-traveler tracks get special styling - use theme warning color
        color = (point as any).trackColor || theme.palette.warning.main;
        opacity = (point as any).trackOpacity || 0.7; // Slightly transparent
      }
      
      // Validate coordinates before creating feature
      // Input coordinates are [lat, lng] format (internal system format)
      // Must convert to [lng, lat] for GeoJSON output
      if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
        return null;
      }
      
      // point.coordinates is [lat, lng] format
      const lat = point.coordinates[0];
      const lng = point.coordinates[1];
      
      if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        return null;
      }
      
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [lng, lat] // GeoJSON format: [lng, lat]
        },
        properties: {
          id: point.id,
          studentId,
          deviceId: point.deviceId,
          deviceType,
          timestamp: point.timestamp.toISOString(),
          timeString: point.timestamp.toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC',
          latitude: lat,
          longitude: lng,
          source: (point as any).source || 'OSINT API',
          color,
          opacity,
          accuracy: point.accuracy,
          activity: point.activity,
          isCoTravelerTrack: isCoTravelerTrack,
          trackType: (point as any).trackType || 'primary',
          metadataJson: point.metadata && Object.keys(point.metadata).length > 0 ? JSON.stringify(point.metadata) : undefined
        }
      };
    }).filter((feature): feature is NonNullable<typeof feature> => feature !== null); // Remove null features from invalid coordinates


    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [dataPoints, enableClustering, showDataPoints, studentColors]);

  // Source key to force remount when clustering mode changes
  const pointsSourceKey = useMemo(() => (enableClustering ? 'points-source-clustered' : 'points-source-plain'), [enableClustering]);

  // Convert routes to GeoJSON
  const routesGeoJSON = useMemo(() => {
    if (routes.length === 0) {
      return {
        type: 'FeatureCollection' as const,
        features: []
      };
    }

    const features = routes
      .filter(route => {
        // Validate route has valid coordinates
        if (!route.coordinates || route.coordinates.length < 2) {
          return false;
        }
        return true;
      })
      .map(route => {
        // Determine if this is a co-traveler route
        const isCoTravelerRoute = (route as any).isCoTravelerRoute === true;
        
        // Use different styling for co-traveler routes
        // Try multiple ID formats for color lookup - match point colors
        // Route studentId IS the raw device/registration ID that points use
        // Points use: studentColors[point.deviceId] where deviceId is the raw registration ID
        // So route.studentId should match point.deviceId
        let color = studentColors[route.studentId] ||  // This should match point.deviceId
                    studentColors[route.deviceId] || 
                    studentColors[route.id] ||
                    // Try the first point's deviceId directly
                    (route.points?.[0] ? studentColors[route.points[0].deviceId] : null) ||
                    // Don't use route.color - it's device-type based (purple/green), not point-based
                    '#FFC107'; // Yellow fallback to match typical point colors
        let opacity = 0.7;
        let width = 2; // Thinner lines
        
        if (isCoTravelerRoute) {
          // Co-traveler routes get special styling - use theme warning color
          color = (route as any).routeColor || theme.palette.warning.main;
          opacity = (route as any).routeOpacity || 0.7;
          width = (route as any).routeWidth || 3;
        }
        
        const deviceType = (route.deviceType || 'phone').toLowerCase();
        
        // Convert coordinates from [lat, lng] to [lng, lat] for GeoJSON
        const geoJsonCoords = route.coordinates
          .filter(coord => coord && coord.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1]))
          .map(coord => [coord[1], coord[0]]); // [lng, lat]
        
        if (geoJsonCoords.length < 2) {
          return null;
        }
        
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: geoJsonCoords
          },
          properties: {
            studentId: route.studentId,
            deviceId: route.deviceId,
            deviceType,
            color,
            opacity,
            width,
            startTime: route.startTime instanceof Date ? route.startTime.toISOString() : String(route.startTime),
            endTime: route.endTime instanceof Date ? route.endTime.toISOString() : String(route.endTime),
            isCoTravelerRoute: isCoTravelerRoute,
            routeType: (route as any).routeType || 'primary'
          }
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null); // Remove null entries

    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [routes, studentColors, showRouteLines]);

  // Create point features at the leading edge (last coordinate) of each route, with bearing for rotation
  const routeHeadsGeoJSON = useMemo(() => {
    if (routes.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const toRad = (d: number) => (d * Math.PI) / 180;
    const toDeg = (r: number) => (r * 180) / Math.PI;

    const features = routes
      .filter(r => (r.coordinates?.length || 0) >= 2)
      .map(r => {
        const coords = r.coordinates; // [lat, lon]
        const last = coords[coords.length - 1];
        const prev = coords[coords.length - 2];
        const lat1 = prev[0];
        const lon1 = prev[1];
        const lat2 = last[0];
        const lon2 = last[1];
        // Bearing calculation (approx)
        const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
        const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
                  Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
        let brng = toDeg(Math.atan2(y, x));
        if (brng < 0) brng += 360;
        const deviceType = (r.deviceType || 'phone').toLowerCase();
        // Use same color lookup logic as routes - match point colors
        const color = studentColors[r.studentId] ||  // This should match point.deviceId
                      studentColors[r.deviceId] || 
                      studentColors[r.id] ||
                      (r.points?.[0] ? studentColors[r.points[0].deviceId] : null) ||
                      // Don't use r.color - it's device-type based, not point-based
                      '#FFC107';
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [last[1], last[0]] // lng, lat
          },
          properties: {
            bearing: brng,
            color,
            deviceType,
            deviceId: r.deviceId,
            studentId: r.studentId
          }
        };
      });

    return { type: 'FeatureCollection' as const, features };
  }, [routes, studentColors]);

  // Co-traveler connections GeoJSON
  const coTravelerConnectionsGeoJSON = useMemo(() => {
    if (!showCoTravelerPoints || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const connectionFeatures = coTravelerResults.flatMap(result => {
      return result.encounters.map(encounter => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [encounter.location[1], encounter.location[0]] // [lng, lat]
        },
        properties: {
          type: 'co-traveler-encounter',
          primaryDevice: result.primaryDevice,
          coTravelerDevice: result.coTravelerDevice,
          timestamp: encounter.timestamp.toISOString(),
          duration: encounter.duration,
          proximity: encounter.proximity,
          confidence: encounter.confidence,
          activity: encounter.activity,
          locationName: encounter.locationName
        }
      }));
    });

    return { type: 'FeatureCollection' as const, features: connectionFeatures };
  }, [coTravelerResults, showCoTravelerPoints]);

  // Co-traveler connection lines GeoJSON
  const coTravelerLinesGeoJSON = useMemo(() => {
    if (!showCoTravelerLines || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const lineFeatures = coTravelerResults.flatMap(result => {
      // Group encounters by time windows to create connection lines
      const timeWindows: { [key: string]: CoTravelerEncounter[] } = {};
      
      result.encounters.forEach(encounter => {
        const timeKey = Math.floor(encounter.timestamp.getTime() / (5 * 60 * 1000)).toString(); // 5-minute windows
        if (!timeWindows[timeKey]) {
          timeWindows[timeKey] = [];
        }
        timeWindows[timeKey].push(encounter);
      });

      return Object.values(timeWindows)
        .filter(encounters => encounters.length >= 2)
        .map(encounters => {
          // Create line connecting encounter locations
          const coordinates = encounters.map(e => [e.location[1], e.location[0]]); // [lng, lat]
          const avgConfidence = encounters.reduce((sum, e) => sum + e.confidence, 0) / encounters.length;
          
          return {
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates
            },
            properties: {
              type: 'co-traveler-connection',
              primaryDevice: result.primaryDevice,
              coTravelerDevice: result.coTravelerDevice,
              encounters: encounters.length,
              confidence: avgConfidence,
              totalDuration: encounters.reduce((sum, e) => sum + e.duration, 0)
            }
          };
        });
    });

    return { type: 'FeatureCollection' as const, features: lineFeatures };
  }, [coTravelerResults, showCoTravelerLines]);

  // Co-traveler heatmap GeoJSON - shows encounter density
  const coTravelerHeatmapGeoJSON = useMemo(() => {
    if (!showCoTravelerHeatmap || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    // Create heatmap points from all co-traveler encounters
    const heatmapFeatures = coTravelerResults.flatMap(result => 
      result.encounters.map(encounter => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [encounter.location[1], encounter.location[0]] // [lng, lat]
        },
        properties: {
          type: 'co-traveler-heatmap',
          primaryDevice: result.primaryDevice,
          coTravelerDevice: result.coTravelerDevice,
          confidence: encounter.confidence,
          duration: encounter.duration,
          timestamp: encounter.timestamp.toISOString(),
          // Weight for heatmap intensity based on confidence and duration
          weight: encounter.confidence * (encounter.duration / 60) // Convert duration to minutes
        }
      }))
    );

    return { type: 'FeatureCollection' as const, features: heatmapFeatures };
  }, [coTravelerResults, showCoTravelerHeatmap]);

  // Enhanced heatmap data processing with performance optimization and time filtering
  const routePointsGeoJSON = useMemo(() => {
    if (routes.length === 0) return { type: 'FeatureCollection' as const, features: [] };
    
    // Performance optimization: Sample points for large datasets
    const maxPointsPerRoute = 100; // Limit points per route for performance
    const features = routes.flatMap(route => {
      const coords = route.coordinates || [];
      if (coords.length === 0) return [];
      
      // Time-based filtering if timeRange is provided
      const routeStartTime = new Date(route.startTime).getTime();
      const routeEndTime = new Date(route.endTime).getTime();
      
      if (timeRange) {
        const timeRangeStart = timeRange.start.getTime();
        const timeRangeEnd = timeRange.end.getTime();
        
        // Skip route if it doesn't overlap with time range
        if (routeEndTime < timeRangeStart || routeStartTime > timeRangeEnd) {
          return [];
        }
      }
      
      // Sample points if route is too long
      const sampledCoords = coords.length > maxPointsPerRoute 
        ? coords.filter((_, index) => index % Math.ceil(coords.length / maxPointsPerRoute) === 0)
        : coords;
      
      return sampledCoords.map(coord => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [coord[1], coord[0]] },
        properties: { 
          deviceId: route.deviceId,
          deviceType: route.deviceType,
          weight: 1, // Base weight for heatmap intensity
          timestamp: routeStartTime // Use route start time for reference
        }
      }));
    });
    
    return { type: 'FeatureCollection' as const, features };
  }, [routes, timeRange]);

  // Generate GeoJSON for dwell locations (transparent red circles)
  const dwellsGeoJSON = useMemo(() => {
    if (!showDwells) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    
    if (dwellLocations.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = dwellLocations.map((dwell, _) => {
      // Calculate radius in meters based on duration (longer dwells = larger circles)
      const durationMinutes = dwell.duration_seconds / 60;
      const baseRadius = 30; // Base radius in meters
      const maxRadius = 150; // Max radius in meters
      // Scale radius based on duration (logarithmic scaling)
      const radius = Math.min(maxRadius, baseRadius + Math.log10(durationMinutes + 1) * 30);

      return {
        type: 'Feature' as const,
        properties: {
          id: dwell.id,
          entity_id: dwell.entity_id,
          device_type: dwell.device_type,
          duration_seconds: dwell.duration_seconds,
          duration_minutes: durationMinutes,
          start_time: dwell.start_time,
          end_time: dwell.end_time,
          radius: radius
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [dwell.longitude, dwell.latitude] // [lon, lat]
        }
      };
    });

    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [dwellLocations, showDwells]);


  // Handle click on points
  const handleClick = useCallback((event: any) => {
    // If in measurement mode, handle point addition
    if (isMeasuring) {
      const { lng, lat } = event.lngLat;
      setMeasurementPoints(prev => [...prev, [lng, lat]]);
      return;
    }
    
    // If in circle drawing mode, handle circle creation
    if (isDrawingMode && drawingShapeType === 'circle' && onCircleDrawn) {
      const { lng, lat } = event.lngLat;
      onCircleDrawn([lat, lng], defaultRadius);
      return;
    }
    
    // If in polygon drawing mode, let Mapbox Draw handle it
    if (isDrawingMode && drawingShapeType === 'polygon') {
      return; // Mapbox Draw will handle polygon creation
    }

    const features = event.features;
    if (!features || features.length === 0) {
    if (showSatelliteLayer && onSatelliteTrackSelect) onSatelliteTrackSelect(null);
    return;
  }
    const feature = features[0];

    if (feature.layer?.id === 'tle-satellites-circle') {
      const props = feature.properties ?? {};
      const raw = props.norad;
      const norad =
        typeof raw === 'number' && Number.isFinite(raw)
          ? raw
          : typeof raw === 'string'
            ? Number(raw)
            : NaN;
      if (onSatelliteTrackSelect) {
        onSatelliteTrackSelect(Number.isFinite(norad) ? norad : null);
      }
      // Show satellite info popup
      const coords = (feature.geometry as any)?.coordinates;
      if (coords) {
        setPopupInfo({
          longitude: coords[0],
          latitude: coords[1],
          properties: { ...props, _isSatellite: true },
        });
      }
      return;
    }

    // Handle cluster click - show carousel popup with all points in cluster
    // Also handle clicks on cluster text label
    if (feature.layer && (feature.layer.id === 'clusters' || feature.layer.id === 'clusters-text')) {
      const clusterId = feature.properties.cluster_id;
      const pointCount = feature.properties.point_count;
      // @ts-ignore - access underlying mapbox map
      const mapboxMap = mapRef.current?.getMap?.();
      
      try {
        const source: any = mapboxMap?.getSource('points-source');
        if (source && source.getClusterLeaves) {
          // Get all points (leaves) in this cluster (limit to 100 for performance)
          const limit = Math.min(pointCount, 100);
          source.getClusterLeaves(clusterId, limit, 0, (err: any, leaves: any[]) => {
            if (err || !leaves || leaves.length === 0) {
              // Fallback: zoom to cluster if can't get leaves
              if (source.getClusterExpansionZoom) {
                source.getClusterExpansionZoom(clusterId, (zoomErr: any, zoom: number) => {
                  if (!zoomErr) {
                    mapboxMap?.easeTo({ center: event.lngLat, zoom });
                  }
                });
              }
              return;
            }
            
            // Set up cluster carousel popup
            const clusterFeatures = leaves.map((leaf: any) => ({
              longitude: leaf.geometry.coordinates[0],
              latitude: leaf.geometry.coordinates[1],
              properties: leaf.properties
            }));
            
            setClusterPoints(clusterFeatures);
            setClusterPointIndex(0);
            setPopupInfo({
              longitude: event.lngLat.lng,
              latitude: event.lngLat.lat,
              isCluster: true,
              clusterSize: pointCount,
              properties: clusterFeatures[0]?.properties || {}
            });
          });
        }
      } catch (e) {
        console.error('Error getting cluster leaves:', e);
      }
      return;
    }

    // Show popup for data layers
    const dataLayers = ['points-layer', 'unclustered-points', 'co-traveler-points', 'co-traveler-connections', 'digital-pol-icons', 'polygon-fills', 'polygon-outlines', 'linestring-lines'];
    
    if (feature.layer && dataLayers.includes(feature.layer.id) && feature.properties) {
      setPopupInfo({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        properties: feature.properties
      });
    }
  }, [isDrawingMode, drawingShapeType, onCircleDrawn, defaultRadius, showSatelliteLayer, onSatelliteTrackSelect, isMeasuring]);
  // FIXED: Reduced performance monitoring (only log when data actually changes)
  useEffect(() => {
    // Only log when significant changes occur, not on every render
    if (dataPoints.length > 0 && Math.random() < 0.1) { // 10% logging frequency
    }
  }, [dataPoints.length, routes.length]);

  // Update viewport when map settings change - ONLY if follow mode is enabled
  useEffect(() => {
    if (followMode) {
      setViewport({
        longitude: mapSettings.center[1],
        latitude: mapSettings.center[0],
        zoom: mapSettings.zoom
      });
    }
  }, [mapSettings, followMode]);

  // Force viewport update when extentUpdateTrigger changes (e.g., for PoL zoom)
  useEffect(() => {
    if (extentUpdateTrigger > 0 && mapRef.current) {
      // Fly to the new location - this updates viewport internally
      mapRef.current.flyTo({
        center: [mapSettings.center[1], mapSettings.center[0]],
        zoom: mapSettings.zoom,
        duration: 1500
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extentUpdateTrigger]); // Only trigger when extentUpdateTrigger changes, not mapSettings

  // Fit to full extent when data loads - DISABLED DURING PLAYBACK
  // This effect is now disabled to prevent auto-fitting while user has manual control
  // Only auto-fit on initial data load (when dataPoints goes from 0 to >0)
  const hasDataPointsRef = React.useRef(false);
  
  useEffect(() => {
    // Only auto-fit once when data first loads, never during playback/updates
    if (hasDataPointsRef.current || !mapRef.current || dataPoints.length === 0) return;
    
    hasDataPointsRef.current = true; // Mark as loaded, never auto-fit again
    
    const map = mapRef.current.getMap?.();
    if (!map) {
      return;
    }
    
    // Extract coordinates with validation (coordinates are internal: [lat, lng])
    const validCoordinates = dataPoints
      .filter(p => p.coordinates && Array.isArray(p.coordinates) && p.coordinates.length >= 2)
      .map(p => ({
        lat: typeof p.coordinates[0] === 'number' ? p.coordinates[0] : null,
        lng: typeof p.coordinates[1] === 'number' ? p.coordinates[1] : null
      }))
      .filter(coord => coord.lat !== null && coord.lng !== null && !isNaN(coord.lat) && !isNaN(coord.lng));
    
    if (validCoordinates.length === 0) {
      return;
    }
    
    const lats = validCoordinates.map(c => c.lat!);
    const lngs = validCoordinates.map(c => c.lng!);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    
    try {
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { 
        padding: 50, 
        duration: 1000
      });
    } catch (error) {
    }
  }, [dataPoints.length > 0]); // Only trigger when dataPoints first becomes non-empty

  // Memoize base map style to avoid reloading style every render
  // Mapbox style URL selection - now using local mapStyle state
  const resolvedMapStyle = useMemo(() => {
    // Satellite-only styles don't contain the `composite/building` source-layer,
    // so 3D buildings won't work. When 3D is enabled, use satellite-streets
    // so the user still gets satellite imagery + vector buildings.
    const effectiveStyle = is3DMode && mapStyle === 'satellite' ? 'satellite-streets' : mapStyle;

    switch (effectiveStyle) {
      case 'satellite':
        return 'mapbox://styles/mapbox/satellite-v9';
      case 'satellite-streets':
        return 'mapbox://styles/mapbox/satellite-streets-v12';
      case 'dark':
        return 'mapbox://styles/mapbox/dark-v11';
      case 'light':
        return 'mapbox://styles/mapbox/light-v11';
      case 'outdoors':
        return 'mapbox://styles/mapbox/outdoors-v12';
      case 'streets':
      default:
        return 'mapbox://styles/mapbox/streets-v12';
    }
  }, [mapStyle, is3DMode]);

  // Add 3D buildings layer
  const add3DBuildingsLayer = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    try {
      if (!map.isStyleLoaded()) return;
      if (map.getLayer('add-3d-buildings')) return;
      // Some styles (e.g., satellite-v9) don't have the composite vector source.
      // In those cases, silently skip 3D buildings.
      if (!map.getSource('composite')) return;

      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer: any) => layer.type === 'symbol' && layer.layout?.['text-field']
      )?.id;

      map.addLayer(
        {
          id: 'add-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          // NOTE: Mapbox's older example uses: ['==', 'extrude', 'true']
          // Newer Mapbox styles (v11/v12) may not include the `extrude` property on buildings.
          // Use a more robust filter so 3D buildings actually render.
          filter: ['all', ['==', '$type', 'Polygon'], ['has', 'height']],
          type: 'fill-extrusion',
          // Mapbox buildings are generally only meaningful when zoomed in.
          // (We can tweak this later, but 15 is a good default.)
          minzoom: 15,
          paint: {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'height']
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'min_height']
            ],
            'fill-extrusion-opacity': 0.6
          }
        },
        labelLayerId
      );

    } catch (error) {
    }
  }, []);

  const apply3DToCurrentStyle = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    if (!map.isStyleLoaded()) return;

    if (is3DModeRef.current) {
      // Ensure terrain is present (react-map-gl props usually handle this, but
      // style changes can temporarily reset terrain until style is fully ready).
      try {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          } as any);
        }
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 } as any);
      } catch (e) {
        // ignore - source may already exist or style not ready
      }

      // Re-add 3D buildings (if supported by the current style)
      add3DBuildingsLayer();

      // Ensure pitch is applied (some style transitions feel like they “flatten”)
      try {
        map.setPitch(45);
      } catch (e) {
      }
    } else {
      // Leaving 3D mode: remove extrusion layer and terrain
      try {
        if (map.getLayer('add-3d-buildings')) {
          map.removeLayer('add-3d-buildings');
        }
      } catch (e) {
      }
      try {
        map.setTerrain(null as any);
      } catch (e) {
      }
    }
  }, [add3DBuildingsLayer]);

  // Keep ref in sync so style.load handler always calls the latest version
  useEffect(() => {
    apply3DToCurrentStyleRef.current = apply3DToCurrentStyle;
  }, [apply3DToCurrentStyle]);

  // Re-apply 3D settings whenever the toggle changes (after the map is loaded)
  useEffect(() => {
    // Update the ref so apply3DToCurrentStyle reads the current value
    is3DModeRef.current = is3DMode;
    
    // Only apply if the map exists and style is loaded
    const map = mapRef.current?.getMap();
    if (map && map.isStyleLoaded()) {
      apply3DToCurrentStyle();
    }
  }, [is3DMode, apply3DToCurrentStyle]);

  // ===== Map Control Functions =====
  
  // Zoom controls
  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap().zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap().zoomOut();
  }, []);

  const handleResetBearing = useCallback(() => {
    mapRef.current?.getMap().resetNorth();
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate distance for measurement tool (returns km)
  const calculateMeasurementDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }, []);

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateMeasurementDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints, calculateMeasurementDistance]);

  // Clear measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  // Toggle measurement mode
  const toggleMeasurement = useCallback(() => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  }, [isMeasuring, clearMeasurement]);

  // Format distance for display
  const formatDistance = useCallback((km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  }, []);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    if (is3DMode) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    }
    
    const map = mapRef.current?.getMap();
    
    // Emit initial bounds on map load
    if (map && onMapChange) {
      const mapBounds = map.getBounds();
      if (mapBounds) {
        const bounds: MapBounds = {
          north: mapBounds.getNorth(),
          south: mapBounds.getSouth(),
          east: mapBounds.getEast(),
          west: mapBounds.getWest(),
        };
        const center = map.getCenter();
        onMapChange([center.lat, center.lng], map.getZoom(), bounds);
      }
    }
    
    // Expose map instance for client-side exports (PowerPoint screenshots, etc.)
    (window as any).__pathfinderMap = map;
    if (map && !styleLoadAttachedRef.current) {
      // Mapbox clears custom layers/sources on style change; re-apply 3D after each style load.
      // IMPORTANT: Use the ref so we always call the LATEST version (avoids stale closure)
      // Also use a small delay to ensure style is fully ready (avoids race conditions)
      const handleStyleLoad = () => {
        // Small delay to ensure map style is fully settled before re-applying 3D
        setTimeout(() => {
          if (is3DModeRef.current) {
            const m = mapRef.current?.getMap();
            if (m) {
              // Force re-add terrain and pitch
              try {
                if (!m.getSource('mapbox-dem')) {
                  m.addSource('mapbox-dem', {
                    type: 'raster-dem',
                    url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                    tileSize: 512,
                    maxzoom: 14,
                  } as any);
                }
                m.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 } as any);
                m.setPitch(45);
              } catch (e) {
                // ignore
              }
              // Re-add 3D buildings
              setTimeout(() => {
                apply3DToCurrentStyleRef.current();
              }, 50);
            }
          }
        }, 100);
      };
      styleLoadHandlerRef.current = handleStyleLoad;
      styleLoadAttachedRef.current = true;
      map.on('style.load', handleStyleLoad);

      // Apply once now too (covers initial load + toggling 3D without style change)
      apply3DToCurrentStyleRef.current();
    }
    
    // Add geocoder control - check if already exists to prevent duplicates
    if (map && !geocoderRef.current) {
      // Clean up any stale geocoder elements from previous renders
      const existingGeocoder = document.querySelector('.mapboxgl-ctrl-geocoder');
      if (existingGeocoder) {
        existingGeocoder.remove();
      }
      
      // Local geocoder function to parse lat/lon coordinates and MGRS
      const coordinateGeocoder = (query: string) => {
        const trimmedQuery = query.trim().toUpperCase();
        
        // Try MGRS first - matches patterns like "33TWN8350424885" or "33T WN 83504 24885"
        // MGRS format: [zone][band][100km square][easting][northing]
        const mgrsPattern = /^(\d{1,2}[C-X])\s*([A-Z]{2})\s*(\d{2,10})$/i;
        const mgrsMatch = trimmedQuery.replace(/\s+/g, ' ').match(mgrsPattern);
        
        if (mgrsMatch || /^\d{1,2}[C-X][A-Z]{2}\d{2,10}$/i.test(trimmedQuery)) {
          try {
            // Normalize MGRS string (remove spaces)
            const mgrsStr = trimmedQuery.replace(/\s+/g, '');
            const [lon, lat] = mgrsToPoint(mgrsStr);
            
            if (!isNaN(lat) && !isNaN(lon)) {
              return [{
                id: `mgrs-${mgrsStr}`,
                center: [lon, lat] as [number, number],
                geometry: {
                  type: 'Point' as const,
                  coordinates: [lon, lat] as [number, number]
                },
                place_name: `🎯 MGRS: ${mgrsStr} (${lat.toFixed(6)}, ${lon.toFixed(6)})`,
                place_type: ['mgrs'],
                properties: {},
                type: 'Feature' as const,
                text: mgrsStr,
                context: [],
                relevance: 1
              }];
            }
          } catch {
            // Invalid MGRS, continue to try lat/lon
          }
        }

        // Try degree/cardinal formats (e.g., "40.7128 N, 74.0060 W" or "40.7128° N 74.0060° W")
        const degreeCardinalPattern = /([\d.]+)\s*°\s*([NS])\s*,?\s*([\d.]+)\s*°\s*([EW])/i;
        const degreeMatch = query.match(degreeCardinalPattern);

        if (degreeMatch) {
          const latValue = parseFloat(degreeMatch[1]);
          const latDir = degreeMatch[2].toUpperCase();
          const lonValue = parseFloat(degreeMatch[3]);
          const lonDir = degreeMatch[4].toUpperCase();

          if (!isNaN(latValue) && !isNaN(lonValue)) {
            const lat = latDir === 'N' ? latValue : -latValue;
            const lon = lonDir === 'E' ? lonValue : -lonValue;

            if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
              return [{
                id: `coord-${lat}-${lon}`,
                center: [lon, lat] as [number, number],
                geometry: {
                  type: 'Point' as const,
                  coordinates: [lon, lat] as [number, number]
                }, 
                place_name: `📍 Coordinates: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                place_type: ['coordinate'],
                properties: {},
                type: 'Feature' as const,
                text: `${lat}, ${lon}`,
                context: [],
                relevance: 1
              }];
            }
          }
        }

        // Match various lat/lon formats:
        // "40.7128, -74.0060" or "40.7128 -74.0060" or "40.7128,-74.0060"
        const matches = query.match(
          /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),?\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/
        );
        
        if (!matches) {
          // Try parsing "lat lon" format with comma or space
          const parts = query.split(/[,\s]+/).filter(p => p.trim());
          if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lon = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lon) && 
                lat >= -90 && lat <= 90 && 
                lon >= -180 && lon <= 180) {
              return [{
                id: `coord-${lat}-${lon}`,
                center: [lon, lat] as [number, number],
                geometry: {
                  type: 'Point' as const,
                  coordinates: [lon, lat] as [number, number]
                },
                place_name: `📍 Coordinates: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                place_type: ['coordinate'],
                properties: {},
                type: 'Feature' as const,
                text: `${lat}, ${lon}`,
                context: [],
                relevance: 1
              }];
            }
          }
          return [];
        }
        
        // Parse the matched coordinates
        const parts = query.split(/[,\s]+/).filter(p => p.trim());
        const lat = parseFloat(parts[0]);
        const lon = parseFloat(parts[1]);
        
        return [{
          id: `coord-${lat}-${lon}`,
          center: [lon, lat] as [number, number],
          geometry: {
            type: 'Point' as const,
            coordinates: [lon, lat] as [number, number]
          },
          place_name: `📍 Coordinates: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
          place_type: ['coordinate'],
          properties: {},
          type: 'Feature' as const,
          text: `${lat}, ${lon}`,
          context: [],
          relevance: 1
        }];
      };
      
      const geocoder = new MapboxGeocoder({
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
        mapboxgl: require('mapbox-gl') as any,
        marker: false,
        placeholder: 'Search address, lat/lon, or MGRS...',
        localGeocoder: coordinateGeocoder,
        // No proximity bias - search globally
      });
      
      // Add event listener to zoom to location when selected
      geocoder.on('result', (e: any) => {
        if (e.result?.center) {
          const [lng, lat] = e.result.center;
          map.flyTo({
            center: [lng, lat],
            zoom: e.result.place_type?.includes('coordinate') ? 15 : 14,
            duration: 1500
          });
        }
      });
      
      map.addControl(geocoder, 'bottom-left');
      geocoderRef.current = geocoder;
      
    }

    // Initialize Mapbox Draw for polygon drawing
    if (map && !drawRef.current) {
      // Define a static mode that doesn't intercept any events
      const staticMode = {
        onSetup: function() {
          return {};
        },
        toDisplayFeatures: function(_: any, __: any, ___: any) {
          // Don't display anything in static mode
        },
        onMouseDown: function() { return false; },
        onMouseUp: function() { return false; },
        onMouseMove: function() { return false; },
        onClick: function() { return false; },
        onKeyUp: function() { return false; },
        onKeyDown: function() { return false; },
        onTouchStart: function() { return false; },
        onTouchMove: function() { return false; },
        onTouchEnd: function() { return false; },
        onTap: function() { return false; },
        onDrag: function() { return false; },
      };
      
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        defaultMode: 'simple_select',
        modes: {
          ...MapboxDraw.modes,
          static: staticMode
        },
        styles: [
          // Polygon fill (inactive)
          {
            'id': 'gl-draw-polygon-fill-inactive',
            'type': 'fill',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            'paint': {
              'fill-color': '#3b9ddd',
              'fill-opacity': 0.2
            }
          },
          // Polygon fill (active)
          {
            'id': 'gl-draw-polygon-fill-active',
            'type': 'fill',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'paint': {
              'fill-color': '#3b9ddd',
              'fill-opacity': 0.3
            }
          },
          // Polygon outline (inactive)
          {
            'id': 'gl-draw-polygon-stroke-inactive',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': '#3b9ddd',
              'line-width': 2
            }
          },
          // Polygon outline (active)
          {
            'id': 'gl-draw-polygon-stroke-active',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': '#3b9ddd',
              'line-width': 3
            }
          },
          // Vertex points (inactive)
          {
            'id': 'gl-draw-polygon-and-line-vertex-inactive',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 5,
              'circle-color': '#fff',
              'circle-stroke-color': '#3b9ddd',
              'circle-stroke-width': 2
            }
          },
          // Vertex points (active)
          {
            'id': 'gl-draw-polygon-and-line-vertex-active',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['==', 'active', 'true']],
            'paint': {
              'circle-radius': 6,
              'circle-color': '#fff',
              'circle-stroke-color': '#3b9ddd',
              'circle-stroke-width': 3
            }
          },
          // Midpoints (for adding new vertices)
          {
            'id': 'gl-draw-polygon-midpoint',
            'type': 'circle',
            'filter': ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
            'paint': {
              'circle-radius': 3,
              'circle-color': '#3b9ddd'
            }
          }
        ]
      });
      
      map.addControl(draw as any, 'top-left');
      drawRef.current = draw;
      
      // If we're already in polygon mode, activate it after initialization
      if (isDrawingMode && drawingShapeType === 'polygon') {
        setTimeout(() => {
          draw.changeMode('draw_polygon');
        }, 100);
      }
      
      // NOTE: Event handlers for draw.create and draw.update are attached in a separate useEffect
      // (see useEffect with dependencies [onCircleDrawn, onCircleUpdate]) to properly handle
      // callback updates and cleanup. Do NOT attach handlers here to avoid duplicates.
    }

    // Geocoder result handler (for address search)
    if (map && geocoderRef.current) {
      // Handle geocoder results (when user searches for an address)
      geocoderRef.current.on('result', (e: any) => {
        const coords = e.result.geometry.coordinates;
        // coords are in [lng, lat] format (GeoJSON)
        const lat = coords[1];
        const lng = coords[0];
        const placeName = e.result.place_name || e.result.text || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        
        // Fly to the location
        map.flyTo({
          center: [lng, lat],
          zoom: 16,
          duration: 1500
        });
        
        // Notify parent to create a point at this location
        if (onLocationSearch) {
          onLocationSearch({
            lat,
            lng,
            name: placeName,
            source: 'address'
          });
        }
      });
    }
  }, [is3DMode, add3DBuildingsLayer, isDrawingMode, drawingShapeType, onLocationSearch]);

  // Cleanup style.load handler on unmount
  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap();
      if (map && styleLoadHandlerRef.current) {
        map.off('style.load', styleLoadHandlerRef.current);
      }
      styleLoadHandlerRef.current = null;
      styleLoadAttachedRef.current = false;
    };
  }, []);

  // FIX: Re-attach MapboxDraw event handlers when callbacks change (fixes polygon drawing after theme change)
  useEffect(() => {
    const map = mapRef.current?.getMap();
    const draw = drawRef.current;
    
    if (!map || !draw) return;
    
    // Remove previous handlers if they exist
    if (drawCreateHandlerRef.current) {
      map.off('draw.create', drawCreateHandlerRef.current);
    }
    if (drawUpdateHandlerRef.current) {
      map.off('draw.update', drawUpdateHandlerRef.current);
    }
    
    // Create new handlers with current callbacks
    const handleDrawCreate = (e: any) => {
      if (onCircleDrawn && e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (feature.geometry.type === 'Polygon') {
          // Calculate centroid of polygon
          const coords = feature.geometry.coordinates[0];
          let sumLat = 0, sumLng = 0;
          coords.forEach((coord: number[]) => {
            sumLng += coord[0];
            sumLat += coord[1];
          });
          const centroid: [number, number] = [sumLat / coords.length, sumLng / coords.length];
          
          // Calculate approximate radius for polygon (for area search compatibility)
          let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
          coords.forEach((coord: number[]) => {
            minLng = Math.min(minLng, coord[0]);
            maxLng = Math.max(maxLng, coord[0]);
            minLat = Math.min(minLat, coord[1]);
            maxLat = Math.max(maxLat, coord[1]);
          });
          
          // Calculate diagonal distance in meters (approximate)
          const latDiff = maxLat - minLat;
          const lngDiff = maxLng - minLng;
          const diagonalDegrees = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
          const approximateRadius = Math.max(100, Math.round(diagonalDegrees * 111000 / 2)); // degrees to meters, minimum 100m
          
          // Pass polygon to parent with approximate radius for search compatibility
          onCircleDrawn(centroid, approximateRadius, feature.geometry.coordinates);
          
          // Clear the drawing
          draw.deleteAll();
        }
      }
    };
    
    const handleDrawUpdate = (e: any) => {
      if (onCircleUpdate && e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (feature.geometry.type === 'Polygon') {
          // Find the circle being edited using the circleId stored in properties
          // or fall back to feature.id or the current editingCircleId
          const circleId = feature.properties?.circleId || feature.id || editingCircleIdRef.current;
          
          if (!circleId) {
            return;
          }
          
          // Calculate centroid of updated polygon
          const coords = feature.geometry.coordinates[0];
          let sumLat = 0, sumLng = 0;
          coords.forEach((coord: number[]) => {
            sumLng += coord[0];
            sumLat += coord[1];
          });
          const centroid: [number, number] = [sumLat / coords.length, sumLng / coords.length];
          
          // Calculate approximate radius
          let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
          coords.forEach((coord: number[]) => {
            minLng = Math.min(minLng, coord[0]);
            maxLng = Math.max(maxLng, coord[0]);
            minLat = Math.min(minLat, coord[1]);
            maxLat = Math.max(maxLat, coord[1]);
          });
          const latDiff = maxLat - minLat;
          const lngDiff = maxLng - minLng;
          const diagonalDegrees = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
          const approximateRadius = Math.max(100, Math.round(diagonalDegrees * 111000 / 2));
          
          // Update the circle with new coordinates
          onCircleUpdate(circleId, {
            center: centroid,
            radius: approximateRadius,
            coordinates: feature.geometry.coordinates
          });
        }
      }
    };
    
    // Store handlers in refs
    drawCreateHandlerRef.current = handleDrawCreate;
    drawUpdateHandlerRef.current = handleDrawUpdate;
    
    // Attach new handlers
    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    
    // Cleanup function
    return () => {
      if (drawCreateHandlerRef.current) {
        map.off('draw.create', drawCreateHandlerRef.current);
      }
      if (drawUpdateHandlerRef.current) {
        map.off('draw.update', drawUpdateHandlerRef.current);
      }
    };
  }, [onCircleDrawn, onCircleUpdate]);

  // Handle drawing mode activation/deactivation
  useEffect(() => {
    
    if (!drawRef.current) {
      return;
    }
    
    const draw = drawRef.current;
    
    // Don't interfere if we're editing a polygon
    if (editingCircleId && drawCircles.find(c => c.id === editingCircleId && c.shapeType === 'polygon')) {
      return;
    }
    
    if (isDrawingMode && drawingShapeType === 'polygon') {
      // Enable polygon drawing mode
      draw.changeMode('draw_polygon');
    } else if (isDrawingMode && drawingShapeType === 'circle') {
      // For circle mode, we use map click events, so put draw in static mode
      // to prevent it from intercepting events
      draw.deleteAll();
      try {
        draw.changeMode('static');
      } catch (e) {
        // static mode may not be available in all versions
        draw.changeMode('simple_select');
      }
    } else {
      // Disable drawing mode - use static mode to not intercept map panning
      draw.deleteAll();
      try {
        draw.changeMode('static');
      } catch (e) {
        // static mode may not be available in all versions
        draw.changeMode('simple_select');
      }
    }
  }, [isDrawingMode, drawingShapeType, editingCircleId, drawCircles]);

  // Set crosshair cursor on canvas when in circle drawing mode
  // MapboxDraw handles polygon cursor automatically, but circle mode needs manual cursor management
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    
    const canvas = map.getCanvas();
    if (!canvas) return;
    
    if (isDrawingMode && drawingShapeType === 'circle') {
      canvas.style.cursor = 'crosshair';
    } else if (isMeasuring) {
      canvas.style.cursor = 'crosshair';
    } else if (!isDrawingMode || drawingShapeType !== 'polygon') {
      // Reset cursor when not in any drawing mode (polygon mode is handled by MapboxDraw)
      canvas.style.cursor = '';
    }
    
    return () => {
      // Cleanup: reset cursor when effect is cleaned up
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [isDrawingMode, drawingShapeType, isMeasuring]);

  // Handle polygon editing mode
  useEffect(() => {
    if (!drawRef.current || !editingCircleId || !mapRef.current) {
      return;
    }
    
    const circle = drawCircles.find(c => c.id === editingCircleId);
    if (!circle) {
      return;
    }
    
    const draw = drawRef.current;
    const map = mapRef.current.getMap();
    
    if (circle.shapeType === 'polygon' && circle.coordinates) {
      // Load polygon into MapboxDraw for editing
      
      // Ensure polygon coordinates are closed (first and last point should be the same)
      let polygonCoords = circle.coordinates;
      if (polygonCoords[0] && polygonCoords[0].length > 0) {
        const firstRing = polygonCoords[0];
        const firstPoint = firstRing[0];
        const lastPoint = firstRing[firstRing.length - 1];
        
        // Check if polygon is closed
        if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
          polygonCoords = [[...firstRing, firstPoint]];
        }
      }
      
      // Create a GeoJSON feature from the polygon coordinates
      // Store the circle ID in properties so we can track it
      const feature = {
        type: 'Feature' as const,
        id: circle.id,
        geometry: {
          type: 'Polygon' as const,
          coordinates: polygonCoords
        },
        properties: {
          circleId: circle.id // Store circle ID in properties
        }
      };
      
      // Wait for map to be ready, then load polygon
      const loadPolygon = () => {
        try {
          // Clear any existing drawings first
          draw.deleteAll();
          
          // Switch to simple_select mode first to ensure clean state
          draw.changeMode('simple_select');
          
          
          // Add the polygon using set() which replaces all features
          // This ensures the feature is properly loaded for editing
          const featureCollection = {
            type: 'FeatureCollection' as const,
            features: [feature]
          };
          draw.set(featureCollection);
          
          // Wait for the feature to be added and rendered, then switch to edit mode
          setTimeout(() => {
            try {
              // Get all features from MapboxDraw
              const allFeatures = draw.getAll();
              
              if (allFeatures.features.length === 0) {
                return;
              }
              
              // After set(), there should be exactly one feature (the polygon we just added)
              const addedFeature = allFeatures.features[0];
              
              if (addedFeature && addedFeature.geometry.type === 'Polygon') {
                // Use the feature ID that MapboxDraw assigned (might be different from circle.id)
                const featureId = addedFeature.id;
                
                // First, ensure we're in simple_select mode
                draw.changeMode('simple_select');
                
                // Small delay to ensure mode change completes
                setTimeout(() => {
                  try {
                    // Switch to direct_select mode to allow vertex editing
                    // IMPORTANT: direct_select requires an object with featureId property
                    (draw as any).changeMode('direct_select', { featureId: featureId });
                    
                    console.log('[MapGL] Switched to direct_select mode for feature:', featureId);
                    
                    // Verify the mode change worked
                    setTimeout(() => {
                      try {
                        const currentMode = (draw as any).getMode();
                        console.log('[MapGL] Current draw mode:', currentMode);
                        if (currentMode !== 'direct_select') {
                          // Try one more time with a fresh mode change
                          console.log('[MapGL] Retrying direct_select mode...');
                          draw.changeMode('simple_select');
                          setTimeout(() => {
                            (draw as any).changeMode('direct_select', { featureId: featureId });
                          }, 50);
                        }
                      } catch (verifyError) {
                        console.error('[MapGL] Error verifying mode:', verifyError);
                      }
                    }, 100);
                  } catch (modeError) {
                    console.error('[MapGL] Error changing to direct_select:', modeError);
                  }
                }, 100);
              } else {
              }
            } catch (error) {
            }
          }, 500); // Increased timeout to ensure feature is fully added and rendered
        } catch (error) {
        }
      };
      
      // Ensure map is loaded
      if (map.loaded()) {
        loadPolygon();
      } else {
        map.once('load', loadPolygon);
      }
    } else {
      // Not a polygon, clear any drawings
      draw.deleteAll();
      draw.changeMode('simple_select');
    }
    
    // Cleanup when editing ends - save final state before clearing
    return () => {
      if (drawRef.current && editingCircleId && circle) {
        try {
          // Before clearing, save the final state from MapboxDraw
          const allFeatures = drawRef.current.getAll();
          
          // Find the edited feature - check by circleId in properties first, then by ID
          let editedFeature = allFeatures.features.find((f: any) => 
            f.properties?.circleId === circle.id
          );
          
          // If not found, try by feature ID or just get the first polygon
          if (!editedFeature) {
            editedFeature = allFeatures.features.find((f: any) => 
              f.id === circle.id || f.geometry.type === 'Polygon'
            );
          }
          
          if (editedFeature && editedFeature.geometry.type === 'Polygon' && onCircleUpdateRef.current) {
            
            // Calculate centroid and radius from final coordinates
            const coords = editedFeature.geometry.coordinates[0];
            if (coords && coords.length > 0) {
              let sumLat = 0, sumLng = 0;
              coords.forEach((coord: number[]) => {
                sumLng += coord[0];
                sumLat += coord[1];
              });
              const centroid: [number, number] = [sumLat / coords.length, sumLng / coords.length];
              
              // Calculate approximate radius
              let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
              coords.forEach((coord: number[]) => {
                minLng = Math.min(minLng, coord[0]);
                maxLng = Math.max(maxLng, coord[0]);
                minLat = Math.min(minLat, coord[1]);
                maxLat = Math.max(maxLat, coord[1]);
              });
              const latDiff = maxLat - minLat;
              const lngDiff = maxLng - minLng;
              const diagonalDegrees = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
              const approximateRadius = Math.max(100, Math.round(diagonalDegrees * 111000 / 2));
              
              // Save final state
              onCircleUpdateRef.current(circle.id, {
                center: centroid,
                radius: approximateRadius,
                coordinates: editedFeature.geometry.coordinates
              });
              
            }
          }
          
          // Now clear MapboxDraw
          drawRef.current.deleteAll();
          drawRef.current.changeMode('simple_select');
        } catch (error) {
        }
      }
    };
  // IMPORTANT: do not depend on `drawCircles` here.
  // Polygon vertex dragging triggers `onCircleUpdate` -> parent updates `drawCircles`,
  // which would re-run this effect, clear MapboxDraw, and kick us out of edit mode
  // after a single change (the "one vertex then no more edits" bug).
  }, [editingCircleId]);

  return (
      <Box 
        id="pathfinder-map-container"
        ref={mapContainerRef}
        sx={{ 
          width: '100%', 
          height: '100%', 
          position: 'relative',
          backgroundColor: '#f0f0f0' // Light gray background while loading
        }}
      >
        <Map
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          ref={mapRef}
          longitude={viewport.longitude}
          latitude={viewport.latitude}
          zoom={viewport.zoom}
          reuseMaps={true}
          attributionControl={true}
          preserveDrawingBuffer={true}
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: '400px', // Ensure minimum height
            cursor: isMeasuring ? 'crosshair' : (isDrawingMode ? (drawingShapeType === 'circle' ? 'crosshair' : 'default') : 'auto')
          }}
          mapStyle={resolvedMapStyle}
          renderWorldCopies={false}
          terrain={is3DMode ? { source: 'mapbox-dem', exaggeration: 1.5 } : undefined}
          pitch={is3DMode ? 45 : 0}
          bearing={is3DMode ? 0 : 0}
        onMove={(evt: any) => {
          setViewport(evt.viewState);
          // Don't call onMapChange during move - only on moveend to prevent tab flickering
        }}
        onMoveEnd={(evt: any) => {
          if (onMapChange && evt.viewState) {
            // Get map bounds from the map instance
            const map = mapRef.current?.getMap();
            let bounds: MapBounds | undefined;
            if (map) {
              const mapBounds = map.getBounds();
              if (mapBounds) {
                bounds = {
                  north: mapBounds.getNorth(),
                  south: mapBounds.getSouth(),
                  east: mapBounds.getEast(),
                  west: mapBounds.getWest()
                };
              }
            }
            onMapChange([evt.viewState.latitude, evt.viewState.longitude], evt.viewState.zoom, bounds);
          }
        }}
        onClick={handleClick}
        onLoad={(_: any) => {
          handleMapLoad();
        }}
        onError={(_: any) => {
        }}
        interactiveLayerIds={['points-layer', 'unclustered-points', 'clusters', 'clusters-text', 'co-traveler-points', 'co-traveler-connections', 'co-traveler-heatmap-layer', 'dwell-circles', 'digital-pol-icons', 'polygon-fills', 'polygon-outlines','linestring-lines', 'tle-satellites-circle']}
      >
        {/* Scale Control */}
        <ScaleControl position="bottom-right" />

        {/* 3D Terrain Source */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {/* Unified Map Controls Panel */}
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none',
          }}
        >
          {/* Zoom In */}
          <Tooltip title="Zoom In" placement="left">
            <IconButton onClick={handleZoomIn} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Zoom Out */}
          <Tooltip title="Zoom Out" placement="left">
            <IconButton onClick={handleZoomOut} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Reset North */}
          <Tooltip title="Reset North" placement="left">
            <IconButton onClick={handleResetBearing} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <CompassIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="left">
            <IconButton onClick={toggleFullscreen} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

          {/* My Location */}
          <Tooltip title="My Location" placement="left">
            <IconButton
              onClick={() => {
                navigator.geolocation?.getCurrentPosition(
                  (pos) => {
                    mapRef.current?.flyTo({
                      center: [pos.coords.longitude, pos.coords.latitude],
                      zoom: 14,
                      duration: 1000
                    });
                  },
                  (err) => console.error('Geolocation error:', err)
                );
              }}
              size="small"
              sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Go to Coordinates */}
          <Tooltip title="Go to Coordinates" placement="left">
            <IconButton
              onClick={(e) => setCoordDialogAnchor(e.currentTarget)}
              size="small"
              sx={{ 
                borderRadius: 0, 
                p: 1, 
                color: coordDialogAnchor ? 'primary.main' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                bgcolor: coordDialogAnchor ? 'action.selected' : 'transparent'
              }}
            >
              <GoToCoordIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 3D Mode */}
          <Tooltip title={is3DMode ? "Switch to 2D" : "Switch to 3D"} placement="left">
            <IconButton
              onClick={() => setIs3DMode(!is3DMode)}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: is3DMode ? 'primary.main' : 'transparent',
                color: is3DMode ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                '&:hover': {
                  bgcolor: is3DMode ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>3D</Typography>
            </IconButton>
          </Tooltip>

          <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

          {/* Map Style Selector */}
          <Tooltip title="Map Style" placement="left">
            <IconButton
              onClick={(e) => setMapStyleAnchor(e.currentTarget)}
              size="small"
              sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
            >
              <SatelliteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Measure Distance */}
          <Tooltip title={isMeasuring ? "Cancel Measurement" : "Measure Distance"} placement="left">
            <IconButton
              onClick={toggleMeasurement}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: isMeasuring ? 'primary.main' : 'transparent',
                color: isMeasuring ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                '&:hover': {
                  bgcolor: isMeasuring ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <MeasureIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Clear Measurement - only show when there are points */}
          {measurementPoints.length > 0 && (
            <Tooltip title="Clear Measurement" placement="left">
              <IconButton
                onClick={clearMeasurement}
                size="small"
                sx={{ borderRadius: 0, p: 1, color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Toggle Timeline Slider - only show when callback is provided */}
          {onToggleTimelineSlider && (
            <>
              <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />
              <Tooltip title={timelineSliderHidden ? "Show Timeline Slider" : "Hide Timeline Slider"} placement="left">
                <IconButton
                  onClick={onToggleTimelineSlider}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    bgcolor: timelineSliderHidden ? 'warning.main' : 'transparent',
                    color: timelineSliderHidden ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                    '&:hover': {
                      bgcolor: timelineSliderHidden ? 'warning.dark' : 'action.hover',
                    },
                  }}
                >
                  {timelineSliderHidden ? <ShowTimelineIcon fontSize="small" /> : <HideTimelineIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </>
          )}
          {onToggleSatelliteLayer && (
            <>
              <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />
              <Tooltip
                title={
                  satelliteLayerLoading && showSatelliteLayer
                    ? 'Loading satellite positions…'
                    : showSatelliteLayer
                      ? 'Hide satellite positions (TLE)'
                      : 'Show satellite positions (TLE)'
                }
                placement="left"
              >
              <span>
                <IconButton
                  onClick={onToggleSatelliteLayer}
                  size="small"
                  disabled={satelliteLayerLoading}
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    bgcolor: showSatelliteLayer ? 'secondary.main' : 'transparent',
                    color: showSatelliteLayer ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                    opacity: satelliteLayerLoading ? 0.75 : 1,
                    '&:hover': {
                      bgcolor: showSatelliteLayer ? 'secondary.dark' : 'action.hover',
                    },
                  }}
                >
                  <SatelliteAltIcon fontSize="small" />
                </IconButton>
              </span>
              </Tooltip>
            </>
          )}
        </Paper>
        
        {/* Map Style Popover */}
        <Popover
          open={Boolean(mapStyleAnchor)}
          anchorEl={mapStyleAnchor}
          onClose={() => setMapStyleAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: '0.9rem' }}>
              Map Style
            </Typography>
            <Grid container spacing={1}>
              {[
                { value: 'streets', label: 'Streets' },
                { value: 'satellite', label: 'Satellite' },
                { value: 'satellite-streets', label: 'Hybrid' },
                { value: 'dark', label: 'Dark' },
                { value: 'light', label: 'Light' },
                { value: 'outdoors', label: 'Outdoors' }
              ].map((style) => (
                <Grid item xs={6} key={style.value}>
                  <Card
                    onClick={() => {
                      setMapStyle(style.value as MapStyleType);
                      setMapStyleAnchor(null);
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: mapStyle === style.value ? '2px solid #2196f3' : '1px solid #ddd',
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                  >
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {style.label}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Popover>

        {/* Go to Coordinates Popover */}
        <Popover
          open={Boolean(coordDialogAnchor)}
          anchorEl={coordDialogAnchor}
          onClose={() => {
            setCoordDialogAnchor(null);
            setCoordLatInput('');
            setCoordLonInput('');
            setCoordMgrsInput('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="h6" sx={{ mb: 1.5, fontSize: '0.9rem', fontWeight: 600 }}>
              Go to Coordinates
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* MGRS Input */}
              <TextField
                label="MGRS"
                placeholder="e.g., 33TWN8350424885"
                size="small"
                value={coordMgrsInput}
                onChange={(e) => {
                  setCoordMgrsInput(e.target.value.toUpperCase());
                  // Clear lat/lon when typing MGRS
                  if (e.target.value) {
                    setCoordLatInput('');
                    setCoordLonInput('');
                  }
                }}
                fullWidth
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', my: -0.5 }}>
                — or —
              </Typography>
              <TextField
                label="Latitude"
                placeholder="e.g., 40.7128"
                size="small"
                value={coordLatInput}
                onChange={(e) => {
                  setCoordLatInput(e.target.value);
                  // Clear MGRS when typing lat/lon
                  if (e.target.value) setCoordMgrsInput('');
                }}
                type="number"
                inputProps={{ step: 'any', min: -90, max: 90 }}
                fullWidth
              />
              <TextField
                label="Longitude"
                placeholder="e.g., -74.0060"
                size="small"
                value={coordLonInput}
                onChange={(e) => {
                  setCoordLonInput(e.target.value);
                  // Clear MGRS when typing lat/lon
                  if (e.target.value) setCoordMgrsInput('');
                }}
                type="number"
                inputProps={{ step: 'any', min: -180, max: 180 }}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setCoordDialogAnchor(null);
                    setCoordLatInput('');
                    setCoordLonInput('');
                    setCoordMgrsInput('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!coordMgrsInput && (!coordLatInput || !coordLonInput)}
                  onClick={() => {
                    let lat: number | undefined;
                    let lon: number | undefined;
                    let source: 'coordinates' | 'mgrs' = 'coordinates';
                    let name: string = '';
                    
                    // Try MGRS first
                    if (coordMgrsInput) {
                      try {
                        const mgrsStr = coordMgrsInput.replace(/\s+/g, '');
                        const [mgrLon, mgrLat] = mgrsToPoint(mgrsStr);
                        lat = mgrLat;
                        lon = mgrLon;
                        source = 'mgrs';
                        name = `MGRS: ${mgrsStr}`;
                      } catch {
                        // Invalid MGRS
                        return;
                      }
                    } else {
                      lat = parseFloat(coordLatInput);
                      lon = parseFloat(coordLonInput);
                      name = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
                    }
                    
                    if (lat !== undefined && lon !== undefined && 
                        !isNaN(lat) && !isNaN(lon) && 
                        lat >= -90 && lat <= 90 && 
                        lon >= -180 && lon <= 180) {
                      mapRef.current?.flyTo({
                        center: [lon, lat],
                        zoom: 15,
                        duration: 1500
                      });
                      
                      // Notify parent to create a point at this location
                      if (onLocationSearch) {
                        onLocationSearch({
                          lat,
                          lng: lon,
                          name,
                          source
                        });
                      }
                      
                      setCoordDialogAnchor(null);
                      setCoordLatInput('');
                      setCoordLonInput('');
                      setCoordMgrsInput('');
                    }
                  }}
                >
                  Go
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
                Tip: Also works in search bar (e.g., "33TWN8350424885" or "40.7128, -74.0060")
              </Typography>
            </Box>
          </Paper>
        </Popover>

        {/* Measurement Line */}
        {measurementPoints.length >= 2 && (
          <Source
            id="measurement-line"
            type="geojson"
            data={{
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: measurementPoints
              }
            }}
          >
            <Layer
              id="measurement-line-layer"
              type="line"
              paint={{
                'line-color': '#ff6b35',
                'line-width': 3,
                'line-dasharray': [2, 2]
              }}
            />
          </Source>
        )}

        {/* Measurement Points */}
        {measurementPoints.map((point, index) => (
          <Marker key={`measure-point-${index}`} longitude={point[0]} latitude={point[1]}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#ff6b35',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            />
          </Marker>
        ))}

        {/* Overwatch Article Markers */}
        {showOverwatchArticles && overwatchArticles.length > 0 && overwatchArticles.map((article, index) => {
          if (!article.latitude || !article.longitude) return null;
          return (
            <Marker 
              key={`overwatch-${article.id || index}`} 
              longitude={article.longitude} 
              latitude={article.latitude}
              anchor="center"
              style={{ zIndex: 1000 }}
            >
              <Tooltip title={article.title || 'News Article'} placement="top">
                <Box
                  onClick={() => onArticleClick?.(article)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    fontSize: '24px',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                    '&:hover': {
                      transform: 'scale(1.3)',
                    },
                  }}
                >
                  📰
                </Box>
              </Tooltip>
            </Marker>
          );
        })}

        {/* TLE Satellite Layer */}
        {showSatelliteLayer && satelliteGeoJson.features.length > 0 && (
          <Source id="tle-satellites" type="geojson" data={satelliteGeoJson}>
            <Layer
              id="tle-satellites-circle"
              type="circle"
              paint={{
                'circle-radius': 4,
                'circle-color': '#00e5ff',
                'circle-stroke-width': 1,
                'circle-stroke-color': '#ffffff',
                ...(selectedSatelliteNorad == null
                  ? {
                      'circle-opacity': 0.85,
                      'circle-stroke-opacity': 1,
                    }
                  : {
                      'circle-opacity': [
                        'case',
                        ['==', ['to-number', ['get', 'norad']], selectedSatelliteNorad],
                        1,
                        0.2,
                      ],
                      'circle-stroke-opacity': [
                        'case',
                        ['==', ['to-number', ['get', 'norad']], selectedSatelliteNorad],
                        1,
                        0.25,
                      ],
                    }),
              }}
            />
            </Source>
          )}
          {showSatelliteLayer && satelliteTracksGeoJson.features.length > 0 && (
            <Source id="tle-satellites-tracks" type="geojson" data={satelliteTracksGeoJson}>
              <Layer
                id="tle-satellites-tracks"
                type="line"
                paint={{
                  'line-color': '#00e5ff',
                  'line-width': 2,
                  'line-opacity': 0.55,
                }}
              />
            </Source>
          )}

        {/* Measurement Result Display - fixed at bottom of map */}
        {measurementPoints.length >= 2 && totalDistance > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(255, 107, 53, 0.95)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1000,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {formatDistance(totalDistance)}
            </Typography>
          </Box>
        )}

        {/* Routes Layer - keep mounted to avoid flicker */}
        {/* Enhanced Heatmap Layers - conditional rendering with improved visuals */}
        {showHeatmap && (
          <>
            {/* Route Points Heatmap */}
            <Source id="route-heat" type="geojson" data={routePointsGeoJSON}>
              <Layer
                id="route-heatmap"
                type="heatmap"
                source="route-heat"
                paint={{
                  'heatmap-weight': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.5,
                    12, 1.0,
                    18, 1.5
                  ],
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.4,
                    10, 1.0,
                    15, 1.5,
                    18, 2.0
                  ],
                  'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(0, 0, 255, 0)',           // Transparent blue
                    0.1, 'rgba(0, 100, 255, 0.2)',     // Light blue
                    0.2, 'rgba(0, 150, 255, 0.4)',      // Medium blue
                    0.3, 'rgba(0, 200, 255, 0.6)',      // Cyan
                    0.4, 'rgba(0, 255, 200, 0.7)',      // Light cyan
                    0.5, 'rgba(100, 255, 100, 0.8)',    // Light green
                    0.6, 'rgba(200, 255, 0, 0.8)',      // Yellow-green
                    0.7, 'rgba(255, 200, 0, 0.9)',      // Orange
                    0.8, 'rgba(255, 150, 0, 0.9)',      // Dark orange
                    0.9, 'rgba(255, 100, 0, 0.95)',     // Red-orange
                    1, 'rgba(255, 0, 0, 1)'              // Pure red
                  ],
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 15,
                    10, 25,
                    15, 35,
                    18, 45
                  ],
                  'heatmap-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.6,
                    10, 0.4,
                    15, 0.3,
                    18, 0.2
                  ]
                }}
              />
            </Source>

            {/* Data Points Heatmap (independent of point visibility) */}
            <Source id="points-heat" type="geojson" data={pointsGeoJSON}>
              <Layer
                id="points-heatmap"
                type="heatmap"
                source="points-heat"
                paint={{
                  'heatmap-weight': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.3,
                    12, 0.8,
                    18, 1.2
                  ],
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.3,
                    10, 0.8,
                    15, 1.2,
                    18, 1.5
                  ],
                  'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(255, 0, 255, 0)',         // Transparent magenta
                    0.1, 'rgba(255, 0, 255, 0.1)',   // Light magenta
                    0.2, 'rgba(200, 0, 255, 0.2)',   // Purple
                    0.3, 'rgba(150, 0, 255, 0.3)',   // Medium purple
                    0.4, 'rgba(100, 0, 255, 0.4)',   // Dark purple
                    0.5, 'rgba(0, 0, 255, 0.5)',     // Blue
                    0.6, 'rgba(0, 100, 255, 0.6)',   // Light blue
                    0.7, 'rgba(0, 200, 255, 0.7)',   // Cyan
                    0.8, 'rgba(0, 255, 200, 0.8)',   // Light cyan
                    0.9, 'rgba(100, 255, 100, 0.9)', // Green
                    1, 'rgba(255, 255, 0, 1)'        // Yellow
                  ],
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 10,
                    10, 20,
                    15, 30,
                    18, 40
                  ],
                  'heatmap-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.4,
                    10, 0.3,
                    15, 0.2,
                    18, 0.15
                  ]
                }}
              />
            </Source>
          </>
        )}

        {/* Route Lines - Only render when enabled and has data */}
        {showRouteLines && routes.length > 0 && (
          <>
            <Source id="routes-source" type="geojson" data={routesGeoJSON}>
              <Layer
                id="routes-layer"
                type="line"
                source="routes-source"
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round'
                }}
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['get', 'width'],
                  'line-opacity': ['get', 'opacity']
                }}
              />
            </Source>
            
            {/* Direction arrows at route endpoints - shows where each route ends */}
            {showDirectionArrows && (
              <Source id="route-heads" type="geojson" data={routeHeadsGeoJSON}>
                {/* Arrow using Unicode arrow character */}
                <Layer
                  id="route-arrows"
                  type="symbol"
                  source="route-heads"
                  layout={{
                    'text-field': '➤',
                    'text-size': 16,
                    'text-rotate': ['get', 'bearing'],
                    'text-rotation-alignment': 'map',
                    'text-allow-overlap': true,
                    'text-ignore-placement': true
                  }}
                  paint={{
                    'text-color': ['get', 'color'],
                    'text-halo-color': '#000000',
                    'text-halo-width': 1.5
                  }}
                />
              </Source>
            )}
          </>
        )}

        {/* Points Source and Layers (clustered or plain) - Only show when enabled */}
        {/* Debug: showDataPoints = {showDataPoints} */}
        {showDataPoints && (
          <Source
            id="points-source"
            key={pointsSourceKey}
            type="geojson"
            data={pointsGeoJSON}
            cluster={enableClustering}
            clusterMaxZoom={14}
            clusterRadius={50}
            promoteId="id"
            clusterProperties={{
              // Aggregate the color from clustered points - takes the first color encountered
              // Since points from the same entity have the same color, this preserves entity coloring
              clusterColor: [
                ['coalesce', ['accumulated'], ['get', 'color']],
                ['get', 'color']
              ]
            }}
          >
          {enableClustering ? (
            <>
              <Layer
                id="clusters"
                type="circle"
                source="points-source"
                filter={["has", "point_count"]}
                paint={{
                  // Use entity color from clusterProperties if available, otherwise fallback to default
                  'circle-color': [
                    'coalesce',
                    ['get', 'clusterColor'],
                    '#2196f3'
                  ],
                  'circle-radius': [
                    'interpolate', ['linear'], ['get', 'point_count'],
                    1, 15,
                    50, 20,
                    100, 25,
                    300, 30,
                    800, 35
                  ],
                  'circle-opacity': 0.85,
                  'circle-stroke-color': 'rgba(255,255,255,0.9)',
                  'circle-stroke-width': 2
                }}
              />
              <Layer
                id="clusters-text"
                type="symbol"
                source="points-source"
                filter={["has", "point_count"]}
                layout={{
                  'text-field': ['to-string', ['get', 'point_count_abbreviated']],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 13,
                  'text-allow-overlap': true
                }}
                paint={{ 'text-color': '#ffffff' }}
              />
              {/* Simple high-performance markers */}
              <Layer
                id="unclustered-points"
                type="circle"
                source="points-source"
                filter={["!has", "point_count"]}
                paint={{
                  'circle-radius': Math.max(1, trackPointSize / 2),
                  'circle-color': ['get', 'color'],
                  'circle-opacity': 1,
                  'circle-stroke-color': 'rgba(255, 255, 255, 0.8)',
                  'circle-stroke-width': 0.5
                }}
              />
            </>
          ) : (
            <Layer
              id="points-layer"
              type="circle"
              source="points-source"
                paint={{
                  'circle-radius': trackPointSize,
                  'circle-color': ['get', 'color'],
                  'circle-stroke-color': '#FFFFFF',
                  'circle-stroke-width': 0.5,
                  'circle-opacity': ['get', 'opacity'],
                  'circle-stroke-opacity': 1
                }}
            />
          )}
        </Source>
        )}

        {/* Uploaded GeoJSON Geometries (Polygons, LineStrings, etc.) */}
        {dataPoints.some(p => p._geometry && p._geometryType !== 'Point') && (
          <>
            {/* Polygon Fills and Outlines */}
            <Source
              id="uploaded-polygons"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: dataPoints
                  .filter(p => p._geometry && (p._geometryType === 'Polygon' || p._geometryType === 'MultiPolygon'))
                  .map(p => ({
                    type: 'Feature',
                    geometry: p._geometry,
                    properties: {
                      deviceId: p.deviceId,
                      studentId: p.studentId,
                      color: studentColors[p.deviceId] || '#3388ff',
                      timestamp: p.timestamp?.toISOString(),
                      geometryType: p._geometryType
                    }
                  }))
              }}
            >
              <Layer
                id="polygon-fills"
                type="fill"
                paint={{
                  'fill-color': ['get', 'color'],
                  'fill-opacity': 0.2
                }}
              />
              <Layer
                id="polygon-outlines"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': 2,
                  'line-opacity': 0.8
                }}
              />
            </Source>

            {/* LineStrings and MultiLineStrings */}
            <Source
              id="uploaded-lines"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: dataPoints
                  .filter(p => p._geometry && (p._geometryType === 'LineString' || p._geometryType === 'MultiLineString'))
                  .map(p => ({
                    type: 'Feature',
                    geometry: p._geometry,
                    properties: {
                      deviceId: p.deviceId,
                      studentId: p.studentId,
                      color: studentColors[p.deviceId] || '#3388ff',
                      timestamp: p.timestamp?.toISOString(),
                      geometryType: p._geometryType
                    }
                  }))
              }}
            >
              <Layer
                id="linestring-lines"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': 3,
                  'line-opacity': 0.8
                }}
              />
            </Source>
          </>
        )}

        {/* Co-traveler Visualization Layers */}
        {showCoTravelerPoints && (
          <>
            {/* Co-traveler Encounter Points */}
            <Source id="co-traveler-encounters" type="geojson" data={coTravelerConnectionsGeoJSON}>
              <Layer
                id="co-traveler-points"
                type="circle"
                source="co-traveler-encounters"
                paint={{
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', 'confidence'],
                    0, 4,              // Low confidence - small
                    0.5, 6,            // Medium confidence - medium
                    0.8, 8,            // High confidence - large
                    1, 10              // Perfect confidence - largest
                  ],
                  'circle-color': [
                    'case',
                    ['==', ['get', 'coTravelerEntityType'], 'SRP'], theme.palette.error.main,  // Error for SRP
                    ['==', ['get', 'coTravelerEntityType'], 'RP'], theme.palette.warning.main,    // Warning for RP
                    theme.palette.primary.main  // Primary for Students (default)
                  ],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.8
                }}
              />
              
              {/* Co-traveler Point Labels */}
              <Layer
                id="co-traveler-labels"
                type="symbol"
                source="co-traveler-encounters"
                layout={{
                  'text-field': [
                    'concat',
                    ['get', 'primaryDevice'],
                    ' ↔ ',
                    ['get', 'coTravelerDevice']
                  ],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 10,
                  'text-offset': [0, -2],
                  'text-anchor': 'bottom'
                }}
                paint={{
                  'text-color': '#ffffff',
                  'text-halo-color': '#000000',
                  'text-halo-width': 1
                }}
              />
            </Source>
          </>
        )}

        {/* Co-traveler Connection Lines */}
        {showCoTravelerLines && (
          <>
            <Source id="co-traveler-lines" type="geojson" data={coTravelerLinesGeoJSON}>
              <Layer
                id="co-traveler-connections"
                type="line"
                source="co-traveler-lines"
                paint={{
                  'line-color': [
                    'case',
                    ['==', ['get', 'coTravelerEntityType'], 'SRP'], theme.palette.error.main,  // Error for SRP
                    ['==', ['get', 'coTravelerEntityType'], 'RP'], theme.palette.warning.main,    // Warning for RP
                    theme.palette.primary.main  // Primary for Students (default)
                  ],
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['get', 'confidence'],
                    0, 2,              // Low confidence - thin
                    0.5, 3,            // Medium confidence - medium
                    0.8, 4,            // High confidence - thick
                    1, 5               // Perfect confidence - thickest
                  ],
                  'line-opacity': 0.7
                }}
              />
            </Source>
          </>
        )}

        {/* Co-traveler Heatmap Layer */}
        {showCoTravelerHeatmap && (
          <>
            <Source id="co-traveler-heatmap" type="geojson" data={coTravelerHeatmapGeoJSON}>
              <Layer
                id="co-traveler-heatmap-layer"
                type="heatmap"
                source="co-traveler-heatmap"
                paint={{
                  'heatmap-weight': [
                    'interpolate', ['linear'], ['get', 'weight'],
                    0, 0.1,
                    1, 0.5,
                    5, 1.0,
                    10, 1.5
                  ],
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.3,
                    10, 0.8,
                    15, 1.2,
                    18, 1.5
                  ],
                  'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(255, 0, 0, 0)',           // Transparent red
                    0.1, 'rgba(255, 0, 0, 0.1)',       // Light red
                    0.2, 'rgba(255, 100, 0, 0.2)',     // Orange-red
                    0.3, 'rgba(255, 200, 0, 0.3)',     // Yellow-orange
                    0.4, 'rgba(255, 255, 0, 0.4)',     // Yellow
                    0.5, 'rgba(200, 255, 0, 0.5)',     // Yellow-green
                    0.6, 'rgba(100, 255, 0, 0.6)',     // Green-yellow
                    0.7, 'rgba(0, 255, 0, 0.7)',       // Green
                    0.8, 'rgba(0, 255, 100, 0.8)',     // Green-cyan
                    0.9, 'rgba(0, 255, 200, 0.9)',     // Cyan-green
                    1, 'rgba(0, 255, 255, 1)'          // Pure cyan
                  ],
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 15,
                    10, 25,
                    15, 35,
                    18, 45
                  ],
                  'heatmap-opacity': 0.6
                }}
              />
            </Source>
          </>
        )}

        {/* Dwell Locations (transparent red circles) */}
        {showDwells && dwellLocations.length > 0 && (
          <Source id="dwell-locations" type="geojson" data={dwellsGeoJSON}>
            <Layer
              id="dwell-circles"
              type="circle"
              source="dwell-locations"
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8, 5,  // At zoom 8, 5px radius
                  12, 15, // At zoom 12, 15px radius
                  16, 30, // At zoom 16, 30px radius
                  20, 50  // At zoom 20, 50px radius
                ],
                'circle-color': '#ff0000', // Red color
                'circle-opacity': 0.3, // More visible
                'circle-stroke-color': '#ff0000',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 0.8 // More visible stroke
              }}
            />
          </Source>
        )}

        {/* Digital PoL Activity Markers - Using Mapbox Maki Symbols */}
        {showDigitalPoL && digitalPoLData.length > 0 && (
          <Source 
            id="digital-pol-data" 
            type="geojson" 
            data={{
              type: 'FeatureCollection',
              features: digitalPoLData
                .filter(item => item.latitude && item.longitude)
                .map((item, index) => ({
                  type: 'Feature',
                  properties: {
                    id: item.id || `pol-${index}`,
                    entity_id: item.entity_id,
                    entity_type: item.entity_type,
                    activity_type: item.activity_type,
                    timestamp: item.timestamp,
                    activity_data: JSON.stringify(item.activity_data),
                    // Map activity types to Maki icon names
                    icon: item.activity_type === 'app_info' ? 'mobile-phone' :
                          item.activity_type === 'app_usage' ? 'star' :
                          item.activity_type === 'browsing' ? 'globe' :
                          item.activity_type === 'call' ? 'telephone' :
                          item.activity_type === 'sms' ? 'communications-tower' :
                          item.activity_type === 'status' ? 'fuel' : 'marker',
                    // Map activity types to colors
                    color: item.activity_type === 'app_info' ? '#3b82f6' :
                           item.activity_type === 'app_usage' ? '#8b5cf6' :
                           item.activity_type === 'browsing' ? '#f59e0b' :
                           item.activity_type === 'call' ? '#10b981' :
                           item.activity_type === 'sms' ? '#06b6d4' :
                           item.activity_type === 'status' ? '#ef4444' : '#9e9e9e'
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [item.longitude, item.latitude]
                  }
                }))
            }}
          >
            <Layer
              id="digital-pol-icons"
              type="symbol"
              source="digital-pol-data"
              layout={{
                'icon-image': ['get', 'icon'],
                'icon-size': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 0.8,
                  15, 1.2,
                  20, 1.5
                ],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': '',
                'text-allow-overlap': true
              }}
              paint={{
                'icon-color': ['get', 'color'],
                'icon-halo-color': '#ffffff',
                'icon-halo-width': 2
              }}
            />
          </Source>
        )}

        {/* Circle and Polygon Area Search Layers */}
        {drawCircles.length > 0 && circlesGeoJSON && circlesGeoJSON.features.length > 0 && (
          <>
            {/* Shape Outlines and Fills */}
            <Source id="area-circles" type="geojson" data={circlesGeoJSON}>
              <Layer
                id="circle-outlines"
                type="line"
                source="area-circles"
                paint={{
                  'line-color': [
                    'case',
                    ['get', 'isSaved'], theme.palette.success.main, // Success if saved as geofence
                    ['get', 'hasResults'], theme.palette.warning.main, // Warning if has results
                    theme.palette.primary.main  // Primary if no results yet
                  ],
                  'line-width': [
                    'case',
                    ['get', 'isSaved'], 4, // Thicker line for saved geofences
                    3
                  ],
                  'line-opacity': 0.8
                }}
              />
              <Layer
                id="circle-fills"
                type="fill"
                source="area-circles"
                paint={{
                  'fill-color': [
                    'case',
                    ['get', 'isSaved'], theme.palette.success.main, // Success if saved as geofence
                    ['get', 'hasResults'], theme.palette.warning.main, // Warning if has results
                    theme.palette.primary.main  // Primary if no results yet
                  ],
                  'fill-opacity': 0.1
                }}
              />
            </Source>

            {/* Circle Centers and Labels */}
            <Source id="circle-centers" type="geojson" data={circleCentersGeoJSON}>
              <Layer
                id="circle-centers"
                type="circle"
                source="circle-centers"
                paint={{
                  'circle-radius': 6,
                  'circle-color': [
                    'case',
                    ['get', 'hasResults'], theme.palette.warning.main, // Warning if has results
                    theme.palette.primary.main  // Primary if no results yet
                  ],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2,
                  'circle-opacity': 0.9
                }}
              />
              <Layer
                id="circle-labels"
                type="symbol"
                source="circle-centers"
                layout={{
                  'text-field': [
                    'case',
                    ['get', 'hasResults'],
                    ['concat', ['get', 'name'], '\n', ['get', 'areaLabel'], '\n(', ['to-string', ['get', 'resultCount']], ' entities)'],
                    ['concat', ['get', 'name'], '\n', ['get', 'areaLabel']]
                  ],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 12,
                  'text-offset': [0, 2],
                  'text-anchor': 'top'
                }}
                paint={{
                  'text-color': '#333333',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1
                }}
              />
            </Source>
          </>
        )}

        {/* Circle Editing Handles - Only show for circles being edited */}
        {drawCircles
          .filter(circle => circle.shapeType === 'circle' && editingCircleId === circle.id)
          .map(circle => {
            // Calculate radius handle position (north of center)
            // Convert radius from meters to degrees (approximate)
            const metersPerDegreeLat = 111320;
            const radiusLat = circle.radius / metersPerDegreeLat;
            
            const radiusHandleLat = circle.center[0] + radiusLat;
            const radiusHandleLon = circle.center[1];
            
            // Helper to calculate distance between two lat/lon points in meters
            const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
              const R = 6371000; // Earth radius in meters
              const dLat = (lat2 - lat1) * Math.PI / 180;
              const dLon = (lon2 - lon1) * Math.PI / 180;
              const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
              return R * c;
            };
            
            return (
              <React.Fragment key={`edit-${circle.id}`}>
                {/* Center Marker - Draggable to move circle */}
                <Marker
                  longitude={circle.center[1]}
                  latitude={circle.center[0]}
                  draggable={true}
                  onDragStart={() => {
                    setDraggingCircleId(circle.id);
                    setDragType('center');
                  }}
                  onDrag={(e) => {
                    if (onCircleUpdate && draggingCircleId === circle.id && dragType === 'center') {
                      const newCenter: [number, number] = [e.lngLat.lat, e.lngLat.lng];
                      onCircleUpdate(circle.id, { center: newCenter });
                    }
                  }}
                  onDragEnd={() => {
                    setDraggingCircleId(null);
                    setDragType(null);
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: statusColors.saved,
                      border: '3px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      cursor: 'move',
                      '&:hover': {
                        transform: 'scale(1.2)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  />
                </Marker>
                
                {/* Radius Handle - Draggable to resize circle */}
                <Marker
                  longitude={radiusHandleLon}
                  latitude={radiusHandleLat}
                  draggable={true}
                  onDragStart={() => {
                    setDraggingCircleId(circle.id);
                    setDragType('radius');
                  }}
                  onDrag={(e) => {
                    if (onCircleUpdate && draggingCircleId === circle.id && dragType === 'radius') {
                      const newRadius = haversineDistance(
                        circle.center[0],
                        circle.center[1],
                        e.lngLat.lat,
                        e.lngLat.lng
                      );
                      // Ensure minimum radius of 10m
                      onCircleUpdate(circle.id, { radius: Math.max(10, newRadius) });
                    }
                  }}
                  onDragEnd={() => {
                    setDraggingCircleId(null);
                    setDragType(null);
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: statusColors.hasResults,
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      cursor: 'ns-resize',
                      '&:hover': {
                        transform: 'scale(1.3)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  />
                </Marker>
              </React.Fragment>
            );
          })}

        {/* Pulse Highlight Circle - Shows when zooming to a location */}
        {pulseLocation && (
          <Source
            id="pulse-highlight"
            type="geojson"
            data={{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [pulseLocation.lng, pulseLocation.lat]
              },
              properties: {}
            }}
          >
            <Layer
              id="pulse-circle"
              type="circle"
              paint={{
                'circle-radius': pulseRadius,
                'circle-color': '#00bcd4',
                'circle-opacity': pulseOpacity,
                'circle-stroke-color': '#00bcd4',
                'circle-stroke-width': 3,
                'circle-stroke-opacity': pulseOpacity + 0.2
              }}
            />
          </Source>
        )}

        {/* Popup - Theme-aware styling */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            onClose={() => {
              setPopupInfo(null);
              setClusterPoints([]);
              setClusterPointIndex(0);
            }}
            closeButton={true}
            closeOnClick={false}
            anchor="bottom"
            offset={[0, -15] as [number, number]}
            className={theme.palette.mode === 'dark' ? 'mapboxgl-popup-dark' : ''}
          >
            <div style={{ 
              padding: '12px', 
              minWidth: '380px',
              maxWidth: '450px',
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#ffffff',
              border: 'none',
              borderRadius: '8px',
              outline: 'none',
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 4px 24px rgba(0, 0, 0, 0.8)' 
                : '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              {/* Cluster Navigation Header */}
              {popupInfo.isCluster && clusterPoints.length > 1 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                  paddingBottom: '8px',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <IconButton 
                    size="small" 
                    onClick={() => setClusterPointIndex(prev => prev > 0 ? prev - 1 : clusterPoints.length - 1)}
                    sx={{ 
                      p: 0.5,
                      bgcolor: theme.palette.action.hover,
                      '&:hover': { bgcolor: theme.palette.action.selected }
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    📍 {clusterPointIndex + 1} of {clusterPoints.length}
                    {popupInfo.clusterSize > clusterPoints.length && ` (${popupInfo.clusterSize} total)`}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setClusterPointIndex(prev => prev < clusterPoints.length - 1 ? prev + 1 : 0)}
                    sx={{ 
                      p: 0.5,
                      bgcolor: theme.palette.action.hover,
                      '&:hover': { bgcolor: theme.palette.action.selected }
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              
              {/* Cluster single item header */}
              {popupInfo.isCluster && clusterPoints.length === 1 && (
                <div style={{ 
                  marginBottom: '8px',
                  paddingBottom: '6px',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    📍 Cluster with {popupInfo.clusterSize} point{popupInfo.clusterSize > 1 ? 's' : ''}
                  </Typography>
                </div>
              )}
              
              {(() => {
                // Get the current properties (from cluster point or direct popup)
                const currentProps = popupInfo.isCluster 
                  ? clusterPoints[clusterPointIndex]?.properties 
                  : popupInfo.properties;
                
                if (!currentProps) return null;

                if (currentProps._isSatellite) {
                  const overviewRows: { label: string; value: React.ReactNode }[] = [
                    { label: 'NORAD ID', value: currentProps.norad != null ? String(currentProps.norad) : '–' },
                    { label: 'Orbit Type', value: currentProps.orbitType || '–' },
                    { label: 'Classification', value: currentProps.classification || '–' },
                    { label: 'Intl Designator', value: currentProps.intlDesignator || '–' },
                    { label: 'Latitude', value: currentProps.latitude != null ? `${currentProps.latitude}°` : '–' },
                    { label: 'Longitude', value: currentProps.longitude != null ? `${currentProps.longitude}°` : '–' },
                    { label: 'Epoch', value: currentProps.epoch
                        ? new Date(currentProps.epoch).toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC'
                        : '–' },
                    { label: 'Rev Number', value: currentProps.revNumber != null ? String(currentProps.revNumber) : '–' },
                  ];

                  const orbitalRows: { label: string; value: React.ReactNode }[] = [
                    { label: 'Inclination', value: currentProps.inclination != null ? `${currentProps.inclination}°` : '–' },
                    { label: 'Period', value: currentProps.periodMin != null ? `${currentProps.periodMin} min` : '–' },
                    { label: 'Apogee', value: currentProps.apogeeKm != null ? `${currentProps.apogeeKm} km` : '–' },
                    { label: 'Perigee', value: currentProps.perigeeKm != null ? `${currentProps.perigeeKm} km` : '–' },
                    { label: 'Semi-Major Axis', value: currentProps.semiMajorAxis != null ? `${currentProps.semiMajorAxis} km` : '–' },
                    { label: 'Eccentricity', value: currentProps.eccentricity != null ? String(currentProps.eccentricity) : '–' },
                    { label: 'RAAN', value: currentProps.raan != null ? `${currentProps.raan}°` : '–' },
                    { label: 'Arg of Perigee', value: currentProps.argOfPerigee != null ? `${currentProps.argOfPerigee}°` : '–' },
                    { label: 'Mean Anomaly', value: currentProps.meanAnomaly != null ? `${currentProps.meanAnomaly}°` : '–' },
                    { label: 'Mean Motion', value: currentProps.meanMotion != null ? `${currentProps.meanMotion} rev/day` : '–' },
                    { label: 'BSTAR Drag', value: currentProps.bstar != null ? Number(currentProps.bstar).toExponential(4) : '–' },
                  ];

                  const satTabKeys = ['overview', 'orbital'];
                  const activeSatTab = Math.min(popupTab, satTabKeys.length - 1);

                  const renderRow = (label: string, value: React.ReactNode) => (
                    <span key={label} style={{ display: 'block', marginBottom: 4, color: theme.palette.text.secondary }}>
                      <strong style={{ color: theme.palette.text.primary }}>{label}:</strong> {value}
                    </span>
                  );

                  return (
                    <>
                      <strong style={{ color: '#00e5ff' }}>
                        🛰 {currentProps.name || 'Satellite'}
                      </strong>
                      <br />
                      <Tabs
                        value={activeSatTab}
                        onChange={(_, v: number) => setPopupTab(v)}
                        variant="fullWidth"
                        sx={{
                          minHeight: 36,
                          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem' },
                          '& .MuiTabs-flexContainer': { gap: 0 },
                          marginBottom: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <Tab label="Overview" />
                        <Tab label="Orbital" />
                      </Tabs>
                      <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {satTabKeys[activeSatTab] === 'overview' &&
                          overviewRows.map(({ label, value }) => renderRow(label, value))}
                        {satTabKeys[activeSatTab] === 'orbital' &&
                          orbitalRows.map(({ label, value }) => renderRow(label, value))}
                      </div>
                    </>
                  );
                }
                
                if (currentProps.type === 'co-traveler-encounter') {
                  // Co-traveler encounter popup
                  return (
                    <>
                      <strong style={{ color: theme.palette.primary.main }}>Co-Traveler Encounter</strong><br />
                      <strong style={{ color: theme.palette.text.primary }}>Primary:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.primaryDevice}</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Co-Traveler:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.coTravelerDevice}</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Time:</strong> <span style={{ color: theme.palette.text.secondary }}>{new Date(currentProps.timestamp).toLocaleString('en-US', { timeZone: 'UTC', hour12: false })} UTC</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Duration:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.duration?.toFixed(1)} min</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Proximity:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.proximity?.toFixed(0)}m</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Confidence:</strong> <span style={{ color: theme.palette.text.secondary }}>{((currentProps.confidence || 0) * 100).toFixed(0)}%</span><br />
                      {currentProps.activity && (
                        <>
                          <strong style={{ color: theme.palette.text.primary }}>Activity:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.activity}</span><br />
                        </>
                      )}
                      {currentProps.locationName && (
                        <>
                          <strong style={{ color: theme.palette.text.primary }}>Location:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.locationName}</span><br />
                        </>
                      )}
                    </>
                  );
                } else if (currentProps.type === 'co-traveler-connection') {
                  // Co-traveler connection line popup
                  return (
                    <>
                      <strong style={{ color: theme.palette.warning.main }}>🔗 Co-Traveler Connection</strong><br />
                      <strong style={{ color: theme.palette.text.primary }}>Devices:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.primaryDevice} ↔ {currentProps.coTravelerDevice}</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Encounters:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.encounters}</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Total Duration:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.totalDuration?.toFixed(1)} min</span><br />
                      <strong style={{ color: theme.palette.text.primary }}>Confidence:</strong> <span style={{ color: theme.palette.text.secondary }}>{((currentProps.confidence || 0) * 100).toFixed(0)}%</span><br />
                    </>
                  );
                } else if (currentProps.activity_type) {
                  // Digital PoL marker popup
                  return (
                    <>
                      <strong style={{ color: currentProps.color }}>
                        {currentProps.activity_type === 'app_info' ? 'App Info' :
                         currentProps.activity_type === 'app_usage' ? '⏱App Usage' :
                         currentProps.activity_type === 'browsing' ? 'Browsing' :
                         currentProps.activity_type === 'call' ? 'Call' :
                         currentProps.activity_type === 'sms' ? 'SMS' :
                         currentProps.activity_type === 'status' ? 'Status' : 'Activity'}
                      </strong><br />
                      <strong style={{ color: theme.palette.text.primary }}>Entity:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.entity_id}</span><br />
                      {currentProps.entity_type && (
                        <><strong style={{ color: theme.palette.text.primary }}>Type:</strong> <span style={{ color: theme.palette.text.secondary }}>{currentProps.entity_type}</span><br /></>
                      )}
                      <strong style={{ color: theme.palette.text.primary }}>Time:</strong> <span style={{ color: theme.palette.text.secondary }}>{new Date(currentProps.timestamp).toLocaleString('en-US', { timeZone: 'UTC', hour12: false })} UTC</span><br />
                      {currentProps.activity_data && (
                        <>
                          <strong style={{ color: theme.palette.text.primary }}>Details:</strong><br />
                          <div style={{ fontSize: '0.85em', marginLeft: '8px', color: theme.palette.text.secondary }}>
                            {(() => {
                              try {
                                const data = JSON.parse(currentProps.activity_data);
                                return Object.entries(data).map(([key, value]: [string, any]) => (
                                  <div key={key}>• {key}: {String(value || 'N/A').substring(0, 50)}</div>
                                ));
                              } catch {
                                return <div>• {String(currentProps.activity_data).substring(0, 100)}</div>;
                              }
                            })()}
                          </div>
                        </>
                      )}
                    </>
                  );
                } else {
                  // Regular data point popup - show all available properties
                  const meta = (() => {
                    try {
                      const raw = currentProps.metadataJson;
                      return typeof raw === 'string' && raw ? JSON.parse(raw) : {};
                    } catch {
                      return {};
                    }
                  })();
                  const formatCoord = (v: unknown) =>
                    v != null && typeof v === 'number' && !isNaN(v) ? v.toFixed(5) : '–';
                  const formatTime = (v: unknown) => {
                    if (v == null || v === '') return '–';
                    try {
                      const d = typeof v === 'string' ? new Date(v) : v as Date;
                      return d.toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC';
                    } catch {
                      return String(v);
                    }
                  };
                  const regIdRaw = currentProps.studentId || currentProps.deviceId || currentProps.id || '';
                  const regId = typeof regIdRaw === 'string' ? regIdRaw.replace(/^GN-/i, '') : String(regIdRaw);
                  const explore = exploreDeviceDetails?.[regId];

                  const overviewRows: { label: string; value: React.ReactNode }[] = [
                    { label: 'Source', value: currentProps.source || 'OSINT API' },
                    { label: 'Device ID', value: currentProps.studentId || currentProps.deviceId || currentProps.id || '–' },
                    { label: 'Device Type', value: currentProps.deviceType || 'phone' },
                    { label: 'Latitude', value: formatCoord(currentProps.latitude) },
                    { label: 'Longitude', value: formatCoord(currentProps.longitude) },
                    { label: 'Timestamp', value: formatTime(currentProps.timeString) },
                    { label: 'Accuracy (m)', value: currentProps.accuracy != null ? Math.round(Number(currentProps.accuracy)) : '–' },
                    { label: 'Activity', value: currentProps.activity || '–' },
                    { label: 'Country', value: meta.country ?? '–' },
                    { label: 'Carrier', value: meta.carrier ?? '–' },
                    { label: 'Timezone', value: meta.timezone ?? '–' },
                    { label: 'IP Address', value: meta.ip_address ?? meta.ipAddress ?? '–' },
                    {
                      label: 'Forensic Flag',
                      value: (() => {
                        const flags = meta.all_forensic_flags ?? (meta.forensic_flag != null ? [meta.forensic_flag] : []);
                        return Array.isArray(flags) && flags.length > 0 ? flags.join(', ') : '–';
                      })()
                    }
                  ];

                  const deviceUaRows: { label: string; value: React.ReactNode }[] = [];
                  if (explore) {
                    if (explore.userAgentFull != null && explore.userAgentFull !== '') deviceUaRows.push({ label: 'User Agent Full', value: explore.userAgentFull });
                    if (explore.userAgentBrowserFamily != null) deviceUaRows.push({ label: 'Browser Family', value: explore.userAgentBrowserFamily });
                    if (explore.userAgentBrowserVersion != null) deviceUaRows.push({ label: 'Browser Version', value: explore.userAgentBrowserVersion });
                    if (explore.userAgentOSFamily != null) deviceUaRows.push({ label: 'OS Family', value: explore.userAgentOSFamily });
                    if (explore.userAgentOSVersion != null) deviceUaRows.push({ label: 'OS Version', value: explore.userAgentOSVersion });
                    if (explore.userAgentDeviceFamily != null) deviceUaRows.push({ label: 'Device Family', value: explore.userAgentDeviceFamily });
                    if (explore.userAgentDeviceBrand != null) deviceUaRows.push({ label: 'Device Brand', value: explore.userAgentDeviceBrand });
                    if (explore.userAgentDeviceModel != null) deviceUaRows.push({ label: 'Device Model', value: explore.userAgentDeviceModel });
                    deviceUaRows.push({ label: 'Advertiser EU', value: explore.eu === true ? 'Yes' : explore.eu === false ? 'No' : '–' });
                    if (explore.createdOn != null && String(explore.createdOn).trim() !== '') {
                      try {
                        deviceUaRows.push({
                          label: 'Created On',
                          value: new Date(explore.createdOn).toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC',
                        });
                      } catch {
                        deviceUaRows.push({ label: 'Created On', value: String(explore.createdOn) });
                      }
                    }
                    if (explore.updatedOn != null && String(explore.updatedOn).trim() !== '') {
                      try {
                        deviceUaRows.push({
                          label: 'Updated On',
                          value: new Date(explore.updatedOn).toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC',
                        });
                      } catch {
                        deviceUaRows.push({ label: 'Updated On', value: String(explore.updatedOn) });
                      }
                    }
                  }

                  const hasLastIps = explore && Array.isArray(explore.lastSeenIPAddresses) && explore.lastSeenIPAddresses.length > 0;
                  const tabKeys = ['overview', ...(deviceUaRows.length > 0 ? ['device'] : []), ...(hasLastIps ? ['lastIps'] : [])];
                  const activeTab = Math.min(popupTab, Math.max(0, tabKeys.length - 1));

                  const renderRow = (label: string, value: React.ReactNode) => (
                    <span key={label} style={{ display: 'block', marginBottom: 4, color: theme.palette.text.secondary }}>
                      <strong style={{ color: theme.palette.text.primary }}>{label}:</strong> {value}
                    </span>
                  );

                  return (
                    <>
                      <Tabs
                        value={activeTab}
                        onChange={(_, v: number) => setPopupTab(v)}
                        variant="fullWidth"
                        sx={{
                          minHeight: 36,
                          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem' },
                          '& .MuiTabs-flexContainer': { gap: 0 },
                          marginBottom: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <Tab label="Overview" />
                        {deviceUaRows.length > 0 && <Tab label="Device / UA" />}
                        {hasLastIps && <Tab label="Last IPs" />}
                      </Tabs>
                      <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {tabKeys[activeTab] === 'overview' &&
                          overviewRows.map(({ label, value }) => renderRow(label, value))}
                        {tabKeys[activeTab] === 'device' &&
                          deviceUaRows.map(({ label, value }) => renderRow(label, value))}
                        {tabKeys[activeTab] === 'lastIps' && hasLastIps && (
                          <span style={{ display: 'block' }}>
                            {explore!.lastSeenIPAddresses.slice(0, 50).map((entry: any, i: number) => (
                              <span key={i} style={{ display: 'block', fontSize: '0.9em', marginBottom: 2 }}>
                                {entry.ipAddress ?? '–'} {entry.timestamp != null ? `(${formatTime(new Date(entry.timestamp))})` : ''}
                              </span>
                            ))}
                            {explore!.lastSeenIPAddresses.length > 50 && (
                              <span style={{ fontSize: '0.85em', color: theme.palette.text.secondary }}>
                                … and {explore!.lastSeenIPAddresses.length - 50} more
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </>
                  );
                }
              })()}
            </div>
          </Popup>
        )}
      </Map>
    </Box>
  );
};

export default PathfinderMapGL;